import React from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import { observable, action, computed, runInAction } from 'mobx';
import moment from 'moment';
import styled from 'styled-components';
import AdminOverview from '../../component/AdminOverview';
import { PlanningTicket, PlanningTicketStore, Milestone, MilestoneStore } from '../../store/planning';
import { DeveloperAllocation, DeveloperAllocationStore, DeveloperHoliday, DeveloperHolidayStore } from '../../store/planning';
import { User, UserStore } from '../../store/User';
import { Project, ProjectStore } from '../../store/Project';
import TicketEditModal from './TicketEditModal';
import TicketEditPanel from './TicketEditPanel';
import AllocationEditModal from './AllocationEditModal';
import { api } from '../../store/Base';
import { t } from '../../i18n';
import { ActionBtn } from './shared/ActionBtn';
import { userColor, resolveTicketAssigneeId } from '../../component/CapacityPlanner/types';
import BurndownChart from '../../component/Milestone/BurndownChart';
import '../CapacityPlanner/capacity-planner.css';
import { OutlineIcon } from './shared/OutlineIcon';

const C = {
    bg: '#f5f6f7', surface: '#ffffff', border: '#e0e3e8', borderStrong: '#c5cad2',
    textPrimary: '#1a1d23', textSecondary: '#5c6470', textMuted: '#9099a8',
    accent: '#2185d0', accentLight: '#e8f3fb',
    success: '#21ba45', successLight: '#eafbef',
    warning: '#f0a721', warningLight: '#fef5e7',
    danger: '#db2828', weekend: '#f0f2f5', holiday: '#f5e6e6',
    todayLine: '#2185d0', sidebarBg: '#f8f9fa',
    // Stronger, more visible colors for less-frequent states
    otherProject: '#dde6f5', otherProjectBorder: '#94a3b8',
    notAllocated: '#fafbfc', notAllocatedBorder: '#dde1e7',
    unavailable: '#fde8e8', unavailableBorder: '#e6a3a3',
    // Status colors (richer)
    statusTodo: '#6b7280', statusInProgress: '#2563eb', statusReview: '#d97706',
    statusDone: '#16a34a', statusBlocked: '#dc2626',
    // Stripe color for alternating rows
    rowStripe: '#fafbfc',
};

const PALETTE = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#dd6b20', '#319795', '#d53f8c', '#6b46c1'];

function colorFor(s: string): string {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return PALETTE[Math.abs(h) % PALETTE.length];
}

function hexTint(hex: string, alpha: number): string {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex);
    if (!m) return hex;
    const int = parseInt(m[1], 16);
    const r = (int >> 16) & 255, g = (int >> 8) & 255, b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const DEFAULT_DAY_WIDTH = 112;
const VISIBLE_WEEKS = 8;
const ROW_LABEL_W = 200;

// [T54191] Burndown panel under the timeline: drag-resizable via the handle
// between the timeline and the chart, collapsible via the chevron, and the
// preference persists in localStorage.
const BURNDOWN_DEFAULT_H = 280;
const BURNDOWN_MIN_H = 140;
const BURNDOWN_MAX_H = 680;
const BURNDOWN_PREFS_KEY = 'milestone-detail-burndown-prefs';

const clampBurndownHeight = (h: number) =>
    Math.max(BURNDOWN_MIN_H, Math.min(BURNDOWN_MAX_H, Math.round(h)));

function loadBurndownPrefs(): { height: number; collapsed: boolean } {
    try {
        const raw = localStorage.getItem(BURNDOWN_PREFS_KEY);
        if (raw) {
            const prefs = JSON.parse(raw);
            return {
                height: Number.isFinite(prefs?.height) ? clampBurndownHeight(prefs.height) : BURNDOWN_DEFAULT_H,
                collapsed: !!prefs?.collapsed,
            };
        }
    } catch (e) {
        // Corrupt or unavailable localStorage — fall back to the defaults.
    }
    return { height: BURNDOWN_DEFAULT_H, collapsed: false };
}
const DAY_SHORT_MAP: Record<number, string> = { 0: 'Mon', 1: 'Tue', 2: 'Wed', 3: 'Thu', 4: 'Fri', 5: 'Sat', 6: 'Sun' };

function getStartOfWeek(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff); d.setHours(0, 0, 0, 0);
    return d;
}
function addDays(date: Date, days: number): Date {
    const d = new Date(date); d.setDate(d.getDate() + days); return d;
}
function daysBetween(d1: Date, d2: Date): number {
    const a = new Date(d1); a.setHours(0,0,0,0);
    const b = new Date(d2); b.setHours(0,0,0,0);
    return Math.round((b.getTime() - a.getTime()) / (1000*60*60*24));
}
function isToday(date: Date): boolean {
    return new Date(date).toDateString() === new Date().toDateString();
}
function isWeekend(date: Date): boolean {
    const d = new Date(date).getDay();
    return d === 0 || d === 6;
}

const DropRejection = styled.div`
    position: fixed;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    background: #fde8e8;
    color: #9b1c1c;
    border: 1px solid #f56565;
    border-radius: 6px;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    z-index: 300;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    max-width: 560px;
`;

const PqSection = styled.div`
    border-bottom: 1px solid ${C.border};
    padding: 10px 12px 12px;
    background: ${C.surface};
`;
const PqHeader = styled.div`
    display: flex; align-items: center; justify-content: space-between;
    font-size: 13px; font-weight: 700; color: ${C.textPrimary};
    margin-bottom: 8px;
`;

const Wrapper = styled.div`
    /* [T54191] The app shell (html/body/#root + re-cy-cle AppContainer/Body)
       now has a real viewport height, so height: 100% resolves properly:
       the sidebar, timeline and burndown panel scroll internally instead of
       growing the page and dragging the toolbar out from under the app
       header. Same pattern as the Capacity Planner (.cp-app) and
       MilestoneStatusOverview.Shell. */
    display: flex; flex-direction: column; flex: 1; height: 100%; overflow: hidden; background: ${C.bg}; position: relative;
`;
const Toolbar = styled.div`
    display: flex; align-items: center; gap: 8px; padding: 8px 16px;
    border-bottom: 1px solid ${C.border}; background: ${C.surface}; flex-shrink: 0;
`;
const TBtn = styled.button`
    padding: 4px 12px; border: 1px solid ${C.borderStrong}; border-radius: 4px;
    background: ${C.surface}; color: ${C.textPrimary}; cursor: pointer; font-size: 13px;
    &:hover { background: ${C.bg}; }
`;

const ToolbarGroup = styled.div`
    display: inline-flex; align-items: center; gap: 6px;
`;
const ToolbarDivider = styled.div`
    width: 1px; height: 22px; background: ${C.border}; margin: 0 2px;
`;
const WeekLabel = styled.span`
    font-size: 13px; font-weight: 600; color: ${C.textPrimary}; min-width: 200px; text-align: center;
`;
const LegendBar = styled.div`
    display: flex; align-items: center; gap: 12px; padding: 4px 16px;
    border-bottom: 1px solid ${C.border}; background: ${C.surface}; font-size: 11px; color: ${C.textSecondary}; flex-shrink: 0;
`;
const LegendItem = styled.span` display: flex; align-items: center; gap: 4px; `;
const LegendDot = styled.span` width: 12px; height: 12px; border-radius: 2px; display: inline-block; `;
const Body = styled.div` flex: 1; display: flex; overflow: hidden; `;

const Sidebar = styled.div`
    width: 280px; flex-shrink: 0; border-right: 2px solid ${C.border}; background: ${C.surface}; overflow-y: auto;
`;
const SidebarHeader = styled.div`
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    padding: 8px 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
    color: ${C.textSecondary}; background: ${C.sidebarBg}; border-bottom: 1px solid ${C.border};
    /* [T54191] Stay pinned while the tickets below scroll (the sidebar is
       the scroll container). */
    position: sticky; top: 0; z-index: 3;
`;
const StatusCount = styled.span<{ color: string }>`
    display: inline-flex; align-items: center; gap: 3px;
    padding: 2px 7px; border-radius: 10px; font-size: 10px; font-weight: 700;
    background: ${props => props.color};
    color: ${props => props.color === C.statusDone || props.color === C.statusInProgress ? '#fff' : C.textPrimary};
    letter-spacing: 0;
    text-transform: none;
`;
const StatusBadgeRow = styled.div`
    display: flex; gap: 4px; align-items: center; flex-wrap: wrap;
`;
const MsGroup = styled.div` border-bottom: 1px solid ${C.border}; `;
const MsHeader = styled.div`
    padding: 8px 12px; cursor: pointer; display: flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: ${C.textPrimary}; &:hover { background: ${C.bg}; }
`;
const MsMeta = styled.span` font-size: 11px; font-weight: 400; color: ${C.textMuted}; margin-left: auto; `;
const TicketRow = styled.div`
    padding: 4px 12px 4px 28px; display: flex; flex-direction: column; gap: 3px;
    font-size: 12px; color: ${C.textSecondary}; cursor: pointer; &:hover { background: ${C.bg}; }
`;
const TicketRowHeader = styled.div`
    display: flex; align-items: center; gap: 6px; width: 100%;
`;
const TicketProgress = styled.div`
    height: 4px; border-radius: 2px; background: #e5e7eb; width: 100%; flex-shrink: 0;
    overflow: hidden; align-self: center;
`;
const TicketProgressFill = styled.div`
    height: 100%; border-radius: 2px; transition: width .2s ease;
`;
const TicketDot = styled.span`
    width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700; color: #fff;
    line-height: 1;
`;
const TicketScheduledPill = styled.span`
    font-size: 12px; line-height: 1; flex-shrink: 0;
    opacity: 0.6;
`;
const TicketAvatar = styled.span`
    width: 18px; height: 18px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 8px; font-weight: 600; flex-shrink: 0; margin-left: auto;
`;
const TicketHours = styled.span` font-size: 11px; color: ${C.textMuted}; `;

const TimelineArea = styled.div` flex: 1; display: flex; flex-direction: column; overflow: hidden; `;
const TimelineScroll = styled.div` flex: 1; overflow: auto; `;
// [T54191] Drag handle between the timeline and the burndown chart: drag to
// resize the chart; click the chevron (or the whole bar, once collapsed) to
// hide/show it.
const BurndownHandle = styled.div`
    display: flex; align-items: center; justify-content: center;
    height: 16px; flex-shrink: 0; position: relative; background: ${C.sidebarBg};
    border-top: 1px solid ${C.border}; cursor: ns-resize; user-select: none;
    &:hover { background: ${C.accentLight}; }
    &[data-collapsed='true'] { cursor: pointer; }
`;
const BurndownGrip = styled.span`
    width: 48px; height: 4px; border-radius: 2px; background: ${C.borderStrong};
`;
const BurndownToggle = styled.button`
    position: absolute; right: 8px; top: 0; bottom: 0; border: none; background: transparent;
    color: ${C.textSecondary}; font-size: 10px; cursor: pointer; padding: 0 8px; line-height: 1;
    &:hover { color: ${C.accent}; }
`;
const BurndownCollapsedLabel = styled.span`
    font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
    color: ${C.textSecondary};
`;
const BurndownBox = styled.div`
    flex-shrink: 0; overflow: hidden; background: ${C.surface};
`;
const DateHeader = styled.div`
    display: flex; position: sticky; top: 0; z-index: 10; background: ${C.surface}; border-bottom: 2px solid ${C.border};
`;
const DateHeaderLabel = styled.div`
    width: ${ROW_LABEL_W}px; flex-shrink: 0; padding: 8px 12px; font-size: 12px; font-weight: 600;
    text-transform: uppercase; color: ${C.textSecondary}; border-right: 2px solid ${C.border};
    background: ${C.sidebarBg}; display: flex; align-items: center;
`;
const DateCell = styled.div<{ weekend?: boolean; today?: boolean }>`
    width: ${DEFAULT_DAY_WIDTH}px; flex-shrink: 0; text-align: center; border-right: 1px solid ${C.border};
    display: flex; flex-direction: column; justify-content: center; padding: 4px 0;
    background: ${props => { if (props.today) return C.accentLight; return props.weekend ? C.weekend : C.surface; }};
    font-size: 11px; color: ${props => props.today ? C.accent : C.textSecondary};
`;
const DateNum = styled.span<{ today?: boolean }>`
    font-weight: ${props => props.today ? '700' : '400'}; color: ${props => props.today ? C.accent : C.textPrimary}; font-size: 13px;
`;
const GridRow = styled.div<{ stripe?: boolean }>`
    display: flex; position: relative; border-bottom: 1px solid ${C.border}; min-width: fit-content;
    min-height: 80px;
    background: ${props => props.stripe ? C.rowStripe : C.surface};
    &:hover { background: ${C.bg}; }
`;
const RowLabel = styled.div`
    width: ${ROW_LABEL_W}px; flex-shrink: 0; padding: 6px 12px; border-right: 2px solid ${C.border};
    background: ${C.surface}; display: flex; align-items: center; gap: 8px; cursor: pointer; z-index: 5; position: sticky; left: 0;
    &:hover { background: ${C.accentLight}; }
`;
const Avatar = styled.span`
    width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
    color: #fff; font-size: 11px; font-weight: 600; flex-shrink: 0;
`;
const DevInfo = styled.div` display: flex; flex-direction: column; overflow: hidden; `;
const DevName = styled.span`
    font-size: 13px; font-weight: 600; color: ${C.textPrimary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const DevAllocInfo = styled.span` font-size: 10px; color: ${C.textMuted}; `;

const GridCell = styled.div<{ status: string }>`
    width: ${DEFAULT_DAY_WIDTH}px; flex-shrink: 0; border-right: 1px solid ${C.border}; align-self: stretch;
    background: ${props => {
        if (props.status === 'weekend') return C.weekend;
        if (props.status === 'other-project') return C.otherProject;
        if (props.status === 'unavailable') return C.unavailable;
        if (props.status === 'holiday') return C.holiday;
        return 'transparent';
    }};
`;
const TodayLine = styled.div`
    position: absolute; top: 0; bottom: 0; width: 2px; background: ${C.todayLine}; z-index: 5; pointer-events: none;
`;
const DeadlineLine = styled.div`
    position: absolute; top: 0; bottom: 0; width: 3px; z-index: 5; pointer-events: none;
`;
const DeadlineLabel = styled.div`
    position: absolute; top: 2px; z-index: 6; pointer-events: none; font-size: 10px; font-weight: 700;
    padding: 2px 6px; border-radius: 3px; white-space: nowrap; color: #fff;
`;

// Deadline-status colors (kept in one place; used by the status pill below).
const deadlineColor = (s: string): string => {
    if (s === 'overdue') return C.danger;
    if (s === 'at-risk') return '#e07b00';
    return C.success;
};
const deadlineLabel = (s: string): string => {
    if (s === 'overdue') return 'overdue';
    if (s === 'at-risk') return 'at risk';
    return 'on track';
};
const statusFg = (s?: string): string => {
    if (s === 'overdue') return C.danger;
    if (s === 'at-risk') return '#e07b00';
    if (s === 'on-track') return C.success;
    return C.accent;
};
const statusBg = (s?: string): string => {
    if (s === 'overdue') return '#fde8e8';
    if (s === 'at-risk') return '#fef3e0';
    if (s === 'on-track') return '#e8f8e8';
    return C.accentLight;
};

const DeadlineBadge = styled.div<{ status: string }>`
    display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 4px;
    font-size: 12px; font-weight: 600;
    background: ${statusBg};
    color: ${statusFg};
    border: 1px solid ${statusFg};
`;
const NoDataMsg = styled.div` padding: 40px; text-align: center; color: ${C.textMuted}; `;

// [T54194] Custom hover popup for the other-project indicators — replaces the
// native `title` tooltip, which is slow to appear and looks out of place.
// Rendered at a fixed viewport position, pointer-events off so it never
// steals the mouse from the element being hovered.
const HoverTip = styled.div`
    position: fixed; z-index: 1500;
    transform: translate(-50%, -100%);
    background: #fff; border: 1px solid #dde1e6; border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.14);
    padding: 6px 10px; font-size: 11px; line-height: 1.55; color: #374151;
    white-space: nowrap; pointer-events: none;
`;

const STATUS_DOT: Record<string, string> = {
    todo: C.statusTodo, in_progress: C.statusInProgress, review: C.statusReview,
    done: C.statusDone, blocked: C.statusBlocked,
};
const STATUS_ICON: Record<string, string> = {
    todo: '○', in_progress: '◐', review: '◑', done: '●', blocked: '✕',
};
const STATUS_LABEL: Record<string, string> = {
    todo: 'To do', in_progress: 'In progress', review: 'In review',
    done: 'Done', blocked: 'Blocked',
};

@observer
export default class MilestoneDetailOverview extends AdminOverview {
    myFilterKey = 'planning-milestone-detail';
    settings: any[] = []; buttons: any[] = []; toolbar: any[] = []; filters: any[] = [];

    // [T54194] 'milestone.project' needed to detect cross-project scheduled
    // tickets for the other-project day indicators.
    store = new PlanningTicketStore({ relations: ['milestone.project', 'assignee'], limit: false });
    // [T54191] Milestones are sub projects: carry the budget relation and
    // the spent_hours annotation (actually registered hours) so the sidebar
    // can show both milestone and budget progress.
    milestoneStore = new MilestoneStore({
        relations: ['project', 'budget'],
        limit: false,
        params: { include_annotations: 'spent_hours' },
    });
    userStore = new UserStore({ limit: false, relations: ['team'] });
    allocationStore = new DeveloperAllocationStore({ relations: ['entries.project', 'user'], limit: false });
    projectStore = new ProjectStore({ relations: ['company'], limit: false });
    holidayStore = new DeveloperHolidayStore({ limit: false });

    get milestoneId(): number { return Number((this.props as any).match?.params?.id); }

    @observable title = t('nav.planning.milestoneDetail', 'Milestone Detail');
    @observable milestone: Milestone | null = null;
    @observable weekOffset = 0;
    @observable dayWidth = DEFAULT_DAY_WIDTH;
    // [T54157] Shown when a drop is rejected because the day is not allocated (green/amber).
    @observable dropRejection: { message: string; at: number } | null = null;
    // [T54191] Burndown panel: drag-resizable + collapsible, persisted in
    // localStorage. NOTE: mobx legacy decorators run @observable initializers
    // during the superclass constructor, before plain class fields are
    // assigned — so these initializers must not reference `this`.
    @observable burndownHeight = loadBurndownPrefs().height;
    @observable burndownCollapsed = loadBurndownPrefs().collapsed;
    // [T54194] Hover popup content for the other-project indicators
    // (custom tooltip instead of the native `title`).
    @observable hoverTip: { x: number; y: number; lines: string[] } | null = null;

    @action showHoverTip = (e: React.MouseEvent, lines: string[]) => {
        if (!lines || lines.length === 0) return;
        // Clamp so the (centred) popup never leaves the viewport edges.
        const x = Math.max(140, Math.min(e.clientX, window.innerWidth - 140));
        const y = e.clientY - 12;
        // Don't re-render while the cursor drifts inside the same element —
        // only reposition after it has moved more than a few px.
        const cur = this.hoverTip;
        if (cur && cur.lines === lines && Math.abs(cur.x - x) < 15 && Math.abs(cur.y - y) < 15) return;
        this.hoverTip = { x, y, lines };
    };

    @action hideHoverTip = () => { this.hoverTip = null; };

    // ── [T54157] Priority queues (sidebar) ──
    @observable pqDragOverQueue: number | 'unassigned' | null = null;
    @observable pqDragTicketId: number | null = null;
    // [T54191] Where a dragged ticket would land when hovering a chip:
    // before/after that chip — drives the insert indicator line.
    @observable pqInsertHint: { ticketId: number; before: boolean } | null = null;
    @observable queuesSaving = false;
    @observable queuesMessage: { text: string; kind: 'success' | 'warning' | 'error' } | null = null;
    @observable startDate = getStartOfWeek(new Date());
    @observable expandedMilestones: Set<number> = new Set();
    @observable editModalOpen = false;
    @observable editingTicket: PlanningTicket | null = null;
    @observable allocModalOpen = false;
    @observable ticketPanelOpen = false;
    @observable editingAllocation: DeveloperAllocation | null = null;

    async componentDidMount() {
        if (this.milestoneId) {
            // Fetch the single milestone by ID with project.team relation
            try {
                const ms = new Milestone({ id: this.milestoneId } as any, { relations: ['project.team'] } as any) as Milestone;
                await ms.fetch();
                this.milestone = ms;
                this.title = ms.name;
                this.expandedMilestones.add(ms.id);
            } catch (e) { console.warn('milestone fetch failed', e); }
            // Fetch supporting data — all with limit: false to avoid 25-item cap
            try { await this.userStore.fetch(); } catch (e) { console.warn('user fetch failed', e); }
            try {
                if (this.milestone?.project?.id) {
                    // Fetch all milestones for this project (for the sidebar)
                    await this.milestoneStore.fetch({ with: ['project', 'budget'] });
                    await this.allocationStore.fetch();
                    await this.holidayStore.fetch();
                }
            } catch (e) { console.warn('alloc fetch failed', e); }
        }
        await this.fetch();
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    @action handleKeyDown = (e: KeyboardEvent) => {
        // Don't interfere with text inputs
        const target = e.target as HTMLElement;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
        if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); this.prevWeek(); }
        else if (e.key === 'ArrowRight' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); this.nextWeek(); }
        else if (e.key === 't' || e.key === 'T') { e.preventDefault(); this.goToCurrentWeek(); }
        else if (e.key === '+' || e.key === '=') { e.preventDefault(); this.zoomIn(); }
        else if (e.key === '-' || e.key === '_') { e.preventDefault(); this.zoomOut(); }
        else if (e.key === 'Escape') {
            // Close any open edit panel
            if (this.editingTicket) { e.preventDefault(); this.editingTicket = null; }
            else if (this.editingMilestone) { e.preventDefault(); this.editingMilestone = null; }
        }
    };

    async fetch() {
        await this.store.fetch().catch((e: any) => console.warn('ticket fetch failed', e));
        this.adjustStartDateToData();
    }

    @action adjustStartDateToData() {
        // [T54191] The timeline opens on the current week. Only when ALL of
        // THIS milestone's scheduled tickets lie entirely before the current
        // week (a wrapped-up milestone) do we shift the start back to fit
        // them. Previously this scanned EVERY ticket in the store (it holds
        // all milestones) plus allocation start dates, so an unrelated
        // ticket — or a long-ended allocation starting Mar 1 (a Sunday) —
        // dragged the view back months (e.g. to Feb 23) on every load.
        const currentWeek = getStartOfWeek(new Date());
        let earliest: Date | null = null;
        let latestEnd: Date | null = null;
        for (const t of this.tickets) {
            const tAny = t as any;
            if (!tAny.scheduledStart) continue;
            const s = moment(tAny.scheduledStart).toDate();
            const e = tAny.scheduledEnd ? moment(tAny.scheduledEnd).toDate() : s;
            if (!earliest || s < earliest) earliest = s;
            if (!latestEnd || e > latestEnd) latestEnd = e;
        }
        if (earliest && latestEnd && latestEnd < currentWeek) {
            this.startDate = getStartOfWeek(earliest);
            this.weekOffset = Math.round(daysBetween(this.startDate, currentWeek) / 7);
        }
    }

    get tickets(): PlanningTicket[] {
        const all = (this.store.models as PlanningTicket[]) || [];
        return all.filter((t: any) => Number(t.milestone?.id ?? t.milestone) === this.milestoneId);
    }
    get projectMilestones(): Milestone[] {
        const all = (this.milestoneStore.models as Milestone[]) || [];
        const pid = this.milestone?.project?.id;
        if (!pid) return [];
        return all.filter((m: any) => Number(m.project?.id ?? m.project) === Number(pid));
    }
    get users(): User[] { return (this.userStore.models as User[]) || []; }
    get allocations(): DeveloperAllocation[] { return (this.allocationStore.models as DeveloperAllocation[]) || []; }
    get holidays(): DeveloperHoliday[] { return (this.holidayStore.models as DeveloperHoliday[]) || []; }
    get project(): Project | null {
        // Project comes from the milestone's project relation (fetched with team)
        const proj = this.milestone?.project;
        if (!proj || !proj.id) return null;
        return proj as Project;
    }
    get projectColor(): string { return this.project ? colorFor(this.project.name) : C.accent; }

    get allocatedDevelopers(): { user: User; allocation: DeveloperAllocation }[] {
        const pid = this.milestone?.project?.id;
        if (!pid) return [];
        const result: { user: User; allocation: DeveloperAllocation }[] = [];
        const seen = new Set<number>();
        for (const a of this.allocations) {
            const entries = (a as any).entries?.models || (a as any).entries || [];
            const hasProject = entries.some((e: any) => {
                const epid = e.project?.id ?? e.project;
                return Number(epid) === Number(pid);
            });
            if (hasProject) {
                const uid = (a as any).user?.id ?? (a as any).user;
                const user = this.users.find(u => Number(u.id) === Number(uid));
                if (user && !seen.has(user.id)) { seen.add(user.id); result.push({ user, allocation: a }); }
            }
        }
        return result;
    }

    // ── [T54157] Priority-queue helpers (sidebar) ──

    get pqMilestoneTickets(): any[] {
        // PlanningTickets of THIS milestone, ordered by `order`.
        // [T54191] Done tickets are excluded: finished work doesn't need
        // planning, so it must not occupy space in the queues or the
        // unassigned pool. They also stop being sent to schedule_queues,
        // which leaves their assignee/dates untouched.
        const msId = this.milestoneId;
        return (this.store.models as any[])
            .filter(tk => Number(tk.milestone?.id ?? tk.milestone) === msId)
            .filter(tk => tk.status !== 'done')
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    get pqAllocatedDevs(): { user: User; allocation: DeveloperAllocation | null }[] {
        // Devs with an ACTIVE allocation (open-ended or end date today or
        // later) that has an entry for THIS project — the same rule
        // schedule_queues enforces. Expired allocations would render a
        // queue whose every drop/save fails with a 400 (aborting the
        // whole request, other queues included). A dev can have several
        // consecutive allocations, so check all of them, not just the
        // first attached one.
        const pid = Number(this.milestone?.project?.id);
        if (!pid) return [];
        const today = moment().startOf('day');
        const activeByUser = new Map<number, DeveloperAllocation>();
        for (const a of this.allocations) {
            const end = (a as any).endDate;
            if (end && moment(end).isBefore(today)) continue;
            const entries = (a as any).entries?.models || (a as any).entries || [];
            const hasProject = (entries as any[]).some((en: any) =>
                Number(en.project?.id ?? en.project) === pid);
            if (!hasProject) continue;
            const uid = Number((a as any).user?.id ?? (a as any).user);
            if (!activeByUser.has(uid)) activeByUser.set(uid, a);
        }
        return this.teamDevelopers
            .filter(({ user }) => activeByUser.has(Number(user.id)))
            .map(({ user }) => ({ user, allocation: activeByUser.get(Number(user.id)) || null }));
    }

    pqQueuedTicketsFor(devId: number | 'unassigned'): any[] {
        return this.pqMilestoneTickets.filter(tk =>
            devId === 'unassigned'
                ? resolveTicketAssigneeId(tk) === null
                : resolveTicketAssigneeId(tk) === Number(devId));
    }

    get pqInactiveAssignedDevs(): any[] {
        // [T54191] Devs with tickets assigned in this milestone but NO
        // active allocation for the project (allocation ended). Their
        // queue is rendered grayed-out below the active ones — assigned
        // work must not silently disappear from the queues. The chips
        // stay draggable so the tickets can be moved to someone else.
        const activeIds = new Set(this.pqAllocatedDevs.map(d => d.user.id));
        const byId = new Map<number, any>();
        for (const tk of this.pqMilestoneTickets) {
            const aid = resolveTicketAssigneeId(tk);
            if (aid === null || activeIds.has(aid)) continue;
            const user = (tk as any).assignee;
            if (typeof user === 'object' && user && !byId.has(aid)) byId.set(aid, user);
        }
        return [...byId.values()];
    }

    pqQueueFinish(devId: number): Date | null {
        const ends = this.pqQueuedTicketsFor(devId)
            .map(tk => tk.scheduledEnd ? moment(tk.scheduledEnd).toDate() : null)
            .filter((d: Date | null) => d !== null) as Date[];
        if (ends.length === 0) return null;
        return new Date(Math.max(...ends.map(d => d.getTime())));
    }

    @action setPqDragOver = (queue: number | 'unassigned' | null) => { this.pqDragOverQueue = queue; };

    @action setPqInsertHint = (hint: { ticketId: number; before: boolean } | null) => { this.pqInsertHint = hint; };

    onPqDragStart = (e: React.DragEvent, ticketId: number) => {
        this.pqDragTicketId = ticketId;
        (window as any).__msPqDragTicketId = ticketId;
        if (e.dataTransfer) {
            e.dataTransfer.setData('text/plain', String(ticketId));
            e.dataTransfer.effectAllowed = 'move';
        }
    };

    onPqChipDragOver = (e: React.DragEvent, ticketId: number) => {
        // Position-aware drop target: the top half of a chip inserts
        // before it, the bottom half after it.
        e.preventDefault();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const before = e.clientY < rect.top + rect.height / 2;
        if (!this.pqInsertHint || this.pqInsertHint.ticketId !== ticketId || this.pqInsertHint.before !== before) {
            this.setPqInsertHint({ ticketId, before });
        }
    };

    onPqDropAt = (e: React.DragEvent, targetQueue: number | 'unassigned', targetTicketId: number | null) => {
        // targetTicketId null = dropped on the queue itself → append at the end.
        e.preventDefault();
        e.stopPropagation();
        const hint = this.pqInsertHint;
        this.setPqInsertHint(null);
        this.setPqDragOver(null);
        const ticketId = Number(e.dataTransfer?.getData('text/plain') || (window as any).__msPqDragTicketId);
        this.pqDragTicketId = null;
        (window as any).__msPqDragTicketId = null;
        if (!ticketId) return;
        const tk = (this.store.models as any[]).find(x => x.id === ticketId);
        if (!tk) return;
        if (targetTicketId !== null && targetTicketId === ticketId) return; // dropped on itself

        if (targetQueue === 'unassigned') {
            // Optimistic unassign + insert at position
        } else {
            // Allocation rule: only devs allocated to this project can receive tickets
            if (!this.pqAllocatedDevs.some(d => d.user.id === Number(targetQueue))) {
                this.queuesMessage = { text: 'Developer not allocated to this project', kind: 'error' };
                setTimeout(() => { if (this.queuesMessage?.text === 'Developer not allocated to this project') this.queuesMessage = null; }, 4000);
                return;
            }
        }

        // Where the ticket came from (before we move it), to renumber that queue too
        const fromAssigneeId = resolveTicketAssigneeId(tk);
        const sourceQueue: number | 'unassigned' = fromAssigneeId === null ? 'unassigned' : fromAssigneeId;

        // Target queue members without the dragged ticket, in current priority order
        const members = this.pqQueuedTicketsFor(targetQueue).filter(x => x.id !== ticketId);
        let insertIdx = members.length;
        if (targetTicketId !== null) {
            const tIdx = members.findIndex(x => x.id === targetTicketId);
            if (tIdx >= 0) insertIdx = (hint && hint.ticketId === targetTicketId && !hint.before) ? tIdx + 1 : tIdx;
        }
        const next = [...members.slice(0, insertIdx), tk, ...members.slice(insertIdx)];
        // Renumber the target queue in its new priority order
        next.forEach((x, i) => { x.order = i; });
        // Renumber the source queue when the ticket moved between queues
        if (sourceQueue !== targetQueue) {
            this.pqQueuedTicketsFor(sourceQueue)
                .filter(x => x.id !== ticketId)
                .forEach((x, i) => { x.order = i; });
        }

        if (targetQueue === 'unassigned') {
            tk.assignee = null;
            tk.scheduledStart = null;
            tk.scheduledEnd = null;
        } else {
            const match = this.pqAllocatedDevs.find(d => d.user.id === Number(targetQueue));
            if (!match) return;
            tk.assignee = match.user as any;
        }
        this.saveQueues();
    };

    onPqDrop = (e: React.DragEvent, targetQueue: number | 'unassigned') => {
        // Drop on the queue (not on a specific chip): append at the end.
        this.onPqDropAt(e, targetQueue, null);
    };

    saveQueues = async () => {
        if (!this.milestone) return;
        this.queuesSaving = true;
        try {
            // Build ordered id lists per dev in current priority order
            const queues: Record<string, number[]> = {};
            for (const { user } of this.pqAllocatedDevs) {
                const ids = this.pqQueuedTicketsFor(user.id).map(tk => tk.id);
                if (ids.length > 0) queues[String(user.id)] = ids;
            }
            const unassignedIds = this.pqQueuedTicketsFor('unassigned').map(tk => tk.id);
            if (unassignedIds.length > 0) queues['unassigned'] = unassignedIds;

            const res = await api.post(`subproject/${this.milestone.id}/schedule_queues/`, { queues });
            const data = (res as any)?.data ?? res;
            await this.store.fetch();
            const n = data?.scheduled?.length ?? 0;
            const skipped = data?.skipped?.length ?? 0;
            this.queuesMessage = skipped > 0
                ? { text: `Scheduled ${n} tickets (${skipped} skipped — no capacity)`, kind: 'warning' }
                : { text: `Scheduled ${n} tickets`, kind: 'success' };
        } catch (e: any) {
            console.error('schedule_queues failed', e);
            this.queuesMessage = { text: 'Failed to schedule queues: ' + (e?.response?.data?.message || e?.message || ''), kind: 'error' };
        } finally {
            this.queuesSaving = false;
            setTimeout(() => runInAction(() => { this.queuesMessage = null; }), 4000);
        }
    };

    // All developers relevant to this milestone:
    // 1. Developers on the same team as the project
    // 2. Developers who have a ticket assigned to them on this milestone
    // 3. Developers who have an allocation to this project
    get teamDevelopers(): { user: User; allocation: DeveloperAllocation | null }[] {
        const pid = this.milestone?.project?.id;
        if (!pid) return [];
        const proj = this.project;
        const teamId = (proj as any)?.team?.id ?? (proj as any)?.team;
        const result: { user: User; allocation: DeveloperAllocation | null }[] = [];
        const seen = new Set<number>();

        const addUser = (u: User) => {
            if (seen.has(u.id)) return;
            seen.add(u.id);
            const userAlloc = this.allocations.find(a => {
                const auid = (a as any).user?.id ?? (a as any).user;
                return Number(auid) === Number(u.id);
            }) || null;
            result.push({ user: u, allocation: userAlloc });
        };

        // 1. Allocated developers (they have an allocation to this project)
        for (const d of this.allocatedDevelopers) {
            addUser(d.user);
        }

        // 2. Team members (users whose team matches the project's team)
        if (teamId) {
            for (const u of this.users) {
                const utid = (u as any).team?.id ?? (u as any).team;
                if (Number(utid) === Number(teamId)) {
                    addUser(u);
                }
            }
        }

        // 3. Developers who have a ticket assigned to them on this milestone
        for (const t of this.tickets) {
            const assignee = (t as any).assignee;
            const aid = assignee?.id ?? assignee;
            if (aid) {
                const user = this.users.find(u => Number(u.id) === Number(aid));
                if (user) {
                    addUser(user);
                }
            }
        }

        return result;
    }

    getDevTickets(userId: number): PlanningTicket[] {
        return this.tickets.filter((t: any) => Number(t.assignee?.id ?? t.assignee) === Number(userId));
    }

    // [T54194] Tickets of OTHER projects scheduled on this dev — rendered on
    // the timeline as faint see-through outlines so you can see when the dev
    // is already booked for something else.
    getDevOtherProjectTickets(userId: number): PlanningTicket[] {
        const pid = Number(this.milestone?.project?.id);
        if (!pid) return [];
        return (this.store.models as any[]).filter((t: any) =>
            Number(t.assignee?.id ?? t.assignee) === Number(userId)
            && !!t.scheduledStart
            && Number(t.milestone?.project?.id ?? (t.milestone as any)?.project ?? 0) !== pid);
    }

    // Projected completion date: the latest scheduled_end across all milestone tickets
    get projectedCompletion(): Date | null {
        const ends = this.tickets
            .map((t: any) => t.scheduledEnd ? new Date(t.scheduledEnd) : null)
            .filter((d: Date | null) => d !== null) as Date[];
        if (ends.length === 0) return null;
        return new Date(Math.max(...ends.map(d => d.getTime())));
    }

    // Deadline status: compares projected completion to milestone deadline
    get deadlineStatus(): 'no-deadline' | 'on-track' | 'at-risk' | 'overdue' {
        const dl = this.milestone?.deadline;
        if (!dl) return 'no-deadline';
        // [T54191] deadline is a Moment (Casts.date) on the unified sub
        // project store.
        const deadlineDate = (dl as any).toDate ? (dl as any).toDate() : new Date(dl as any);
        const projected = this.projectedCompletion;
        if (!projected) return 'no-deadline';
        // Overdue: projected completion is after the deadline
        if (projected > deadlineDate) return 'overdue';
        // At risk: less than 3 days of buffer, or deadline is within a week and still not done
        const bufferDays = Math.round((deadlineDate.getTime() - projected.getTime()) / (1000 * 60 * 60 * 24));
        if (bufferDays <= 3) return 'at-risk';
        return 'on-track';
    }
    getMilestoneTickets(msId: number): PlanningTicket[] {
        const all = (this.store.models as PlanningTicket[]) || [];
        return all.filter((t: any) => Number(t.milestone?.id ?? t.milestone) === Number(msId));
    }

    // Matches prototype getProjectAvailabilityForDate()
    /** [T54193] The allocation active for this user on the given date
     * (per-date resolution: a dev can have multiple consecutive schedules;
     * the old `.find()` picked only the first one and rendered wrong outside
     * its range). Falls back to the passed-in allocation when no store match. */
    allocationOn(user: User | number, date: Date): DeveloperAllocation | null {
        const uid = Number((user as any)?.id ?? user);
        const iso = moment(date).format('YYYY-MM-DD');
        const candidates = (this.allocations || []).filter(a => {
            const auid = Number((a as any).user?.id ?? (a as any).user);
            if (auid !== uid) return false;
            const s = (a as any).startDate ? moment((a as any).startDate).format('YYYY-MM-DD') : null;
            const e = (a as any).endDate ? moment((a as any).endDate).format('YYYY-MM-DD') : null;
            return s !== null && s <= iso && (e === null || e >= iso);
        });
        if (candidates.length === 0) return null;
        return candidates[candidates.length - 1];
    }

    /** [T54194] What OTHER projects (if any) this dev is allocated to on
     * the given date: [{projectId, name, hours}] from the per-date
     * allocation's entries for that weekday. */
    otherProjectInfo(user: User, date: Date): { projectId: number; name: string; hours: number }[] {
        const alloc = this.allocationOn(user, date);
        if (!alloc) return [];
        const pid = Number(this.milestone?.project?.id);
        const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
        const entries = ((alloc as any).entries?.models || (alloc as any).entries || []) as any[];
        const out: { projectId: number; name: string; hours: number }[] = [];
        for (const e of entries) {
            if (e.dayOfWeek !== dayOfWeek) continue;
            const ePid = Number((e.project as any)?.id ?? e.project ?? 0);
            if (!ePid || ePid === pid) continue;
            const name = (e.project as any)?.name || ('Project #' + ePid);
            const found = out.find(o => o.projectId === ePid);
            if (found) found.hours += Number(e.hours || 0);
            else out.push({ projectId: ePid, name, hours: Number(e.hours || 0) });
        }
        return out;
    }

    /** [T54194] Tickets scheduled on this date for this dev that belong to
     * ANOTHER project (cross-project PlanningTickets). */
    otherProjectTickets(user: User, date: Date): { id: number; title: string; projectName: string; hoursLeft: number }[] {
        const uid = Number((user as any)?.id ?? 0);
        const pid = Number(this.milestone?.project?.id);
        const iso = moment(date).format('YYYY-MM-DD');
        const out: { id: number; title: string; projectName: string; hoursLeft: number }[] = [];
        for (const t of (this.store.models as PlanningTicket[])) {
            const tAny = t as any;
            const aId = Number(tAny.assignee?.id ?? tAny.assignee ?? 0);
            if (aId !== uid) continue;
            const tPid = Number(tAny.milestone?.project?.id ?? (tAny.milestone as any)?.project ?? 0);
            if (!tPid || tPid === pid) continue;
            const s = tAny.scheduledStart ? moment(tAny.scheduledStart).format('YYYY-MM-DD') : null;
            const e = tAny.scheduledEnd ? moment(tAny.scheduledEnd).format('YYYY-MM-DD') : null;
            if (!s || iso < s || (e && iso > e)) continue;
            // [T54082] Actual hours this ticket takes on this date, from the
            // fill-the-day distribution (was: an even split across the span).
            const est = Number(tAny.estimatedHours || 0);
            const spent = Number(tAny.spentHours || 0);
            const fill = this.userDayHours.byDate.get(uid)?.get(iso) || [];
            let hoursLeft = 0;
            for (const [ftid, fh] of fill) {
                if (ftid === Number(t.id)) hoursLeft += fh;
            }
            if (hoursLeft <= 0) {
                // No fill entry (e.g. dev not allocated to this project on
                // this date) — approximate with the old even spread.
                const days = e ? (moment(e).diff(moment(s), 'days') + 1) : 1;
                hoursLeft = Math.max(0, (est - spent) / Math.max(days, 1));
            }
            out.push({
                id: Number(t.id),
                title: tAny.title || ('#' + t.id),
                projectName: tAny.milestone?.project?.name || ('Project #' + tPid),
                hoursLeft: Math.round(hoursLeft * 10) / 10,
            });
        }
        return out;
    }

    getDayStatus(date: Date, user: User, allocation: DeveloperAllocation | null): string {
        allocation = this.allocationOn(user, date) || allocation;
        if (!allocation) {
            return 'not-allocated';
        }
        const uid = user.id;
        for (const h of this.holidays) {
            const huid = (h as any).user?.id ?? (h as any).user;
            if (Number(huid) !== Number(uid)) continue;
            const hs = (h as any).startDate;
            const he = (h as any).endDate;
            if (hs && he) {
                const d = date.toISOString().split('T')[0];
                if (d >= hs && d <= he) return 'holiday';
            }
        }
        const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0=Mon, 6=Sun
        const aStart = (allocation as any).startDate ? moment((allocation as any).startDate).toDate() : null;
        const aEnd = (allocation as any).endDate ? moment((allocation as any).endDate).toDate() : null;
        if (aStart && date < aStart) return 'not-allocated';
        if (aEnd && date > aEnd) return 'not-allocated';
        const pid = this.milestone?.project?.id;
        const entries = (allocation as any).entries?.models || (allocation as any).entries || [];
        // Entries for this day AND this project only
        const projectEntries = entries.filter((e: any) => e.dayOfWeek === dayOfWeek && Number(e.project?.id ?? e.project) === Number(pid));
        if (projectEntries.length > 0) {
            const totalHours = projectEntries.reduce((s: number, e: any) => s + (e.hours || 0), 0);
            return totalHours >= 8 ? 'available' : 'partial';
        }
        // No allocation to THIS project on this day. Check if allocated to another project.
        const otherEntries = entries.filter((e: any) => e.dayOfWeek === dayOfWeek && Number(e.project?.id ?? e.project) !== Number(pid));
        if (otherEntries.length > 0) {
            return 'other-project';
        }
        // No allocation entries at all for this day
        return isWeekend(date) ? 'weekend' : 'unavailable';
    }

    getTimelineDates(): Date[] {
        const dates: Date[] = [];
        for (let i = 0; i < VISIBLE_WEEKS * 7; i++) dates.push(addDays(this.startDate, i));
        return dates;
    }
    get currentWeekLabel(): string {
        const end = addDays(this.startDate, VISIBLE_WEEKS * 7 - 1);
        return this.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            + ' \u2013 ' + end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    @action prevWeek = () => { this.weekOffset--; this.startDate = addDays(getStartOfWeek(new Date()), this.weekOffset * 7); };
    @action nextWeek = () => { this.weekOffset++; this.startDate = addDays(getStartOfWeek(new Date()), this.weekOffset * 7); };
    @action goToCurrentWeek = () => { this.weekOffset = 0; this.startDate = getStartOfWeek(new Date()); };
    @action zoomIn = () => { this.dayWidth = Math.min(this.dayWidth + 16, 160); };
    @action zoomOut = () => { this.dayWidth = Math.max(this.dayWidth - 16, 32); };

    // ── [T54191] Burndown panel resize/collapse ──
    @action setBurndownHeight = (h: number) => {
        this.burndownHeight = clampBurndownHeight(h);
        this.saveBurndownPrefs();
    };

    @action toggleBurndown = () => {
        this.burndownCollapsed = !this.burndownCollapsed;
        this.saveBurndownPrefs();
    };

    saveBurndownPrefs = () => {
        try {
            localStorage.setItem(BURNDOWN_PREFS_KEY, JSON.stringify({
                height: this.burndownHeight,
                collapsed: this.burndownCollapsed,
            }));
        } catch (e) {
            // localStorage unavailable (private mode etc.) — the preference
            // just won't persist across reloads.
        }
    };

    onBurndownHandleMouseDown = (e: React.MouseEvent) => {
        if (this.burndownCollapsed || e.button !== 0) return;
        e.preventDefault();
        const startY = e.clientY;
        const startHeight = this.burndownHeight;
        const onMove = (ev: MouseEvent) => {
            // Dragging up grows the chart (it sits below the handle).
            this.setBurndownHeight(startHeight + (startY - ev.clientY));
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
    };
    get zoomLabel(): string {
        if (this.dayWidth <= 48) return 'Day';
        if (this.dayWidth <= 96) return 'Wide';
        return 'Extra Wide';
    }
    @action toggleMilestone = (id: number) => {
        if (this.expandedMilestones.has(id)) this.expandedMilestones.delete(id);
        else this.expandedMilestones.add(id);
    };

    @action autoAllocate = async () => {
        if (!this.milestoneId) return;
        try { await api.post('subproject/' + this.milestoneId + '/auto_allocate/', {}); await this.store.fetch(); }
        catch (e: any) { console.error('autoAllocate error:', e); alert('Auto-allocation failed: ' + (e?.message || 'unknown error')); }
    };
    @action syncPhabricator = async () => {
        if (!this.milestoneId) return;
        try {
            const res = await api.get('subproject/' + this.milestoneId + '/sync_phabricator/');
            const data = res.data || res;
            await this.store.fetch();
            alert('Phabricator sync complete: ' + (data.created || 0) + ' created, ' + (data.updated || 0) + ' updated, ' + (data.skipped || 0) + ' skipped');
        } catch (e: any) {
            console.error('syncPhabricator error:', e);
            alert('Sync failed: ' + (e?.message || 'unknown error'));
        }
    };
    @action backToProject = () => {
        const pid = this.milestone?.project?.id;
        if (pid) (this.props as any).history?.push('/planning/project/' + pid + '/overview');
    };

    // ── [T54082] Fill-the-day planning model ──────────────────────────────
    // A dev's day is always filled to capacity before hours spill over into
    // the next day: a 10h ticket on an 8h/day dev is planned as 8h + 2h, and
    // if 4h are already planned on the first day the next ticket takes the
    // remaining 4h there and 6h the day after. This matches the backend's
    // schedule_queues algorithm and replaces the old even spread (10h over
    // 2 days = 5h + 5h), which neither filled anyone's day nor accounted
    // for partially planned days.

    /** Total hours the dev can work on `date`: the sum of that day's
     * allocation entries (all projects), but only on days allocated to
     * THIS project (green/amber on the timeline). */
    dayCapacityHours(user: User, allocation: DeveloperAllocation | null, date: Date): number {
        const status = this.getDayStatus(date, user, allocation);
        if (status !== 'available' && status !== 'partial') return 0;
        const alloc = this.allocationOn(user, date) || allocation;
        if (!alloc) return 0;
        const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
        const entries = (alloc as any).entries?.models || (alloc as any).entries || [];
        const dayEntries = entries.filter((e: any) => e.dayOfWeek === dayOfWeek);
        return dayEntries.reduce((s: number, e: any) => s + (e.hours || 0), 0) || 8;
    }

    /** [T54082] Per-dev per-day hour distribution with fill-the-day
     * semantics. Each dev's tickets are processed chronologically (start
     * date, then queue order, then id) and every ticket consumes the
     * leftover capacity of a day before anything spills into the next, so
     * two tickets can share one day (4h + 4h) without overbooking it.
     * A @computed: cached until tickets/allocations/holidays change.
     *   byDate:   userId → 'YYYY-MM-DD' → [ticketId, hours][]
     *   byTicket: userId → ticketId → [{date, hours}] (chronological) */
    @computed get userDayHours(): {
        byDate: Map<number, Map<string, [number, number][]>>;
        byTicket: Map<number, Map<number, { date: Date; hours: number }[]>>;
    } {
        const byDate = new Map<number, Map<string, [number, number][]>>();
        const byTicket = new Map<number, Map<number, { date: Date; hours: number }[]>>();
        const byUser = new Map<number, PlanningTicket[]>();
        for (const t of (this.store.models as PlanningTicket[])) {
            const tAny = t as any;
            const uid = Number(tAny.assignee?.id ?? tAny.assignee ?? 0);
            if (!uid || !tAny.scheduledStart) continue;
            if (!byUser.has(uid)) byUser.set(uid, []);
            (byUser.get(uid) as PlanningTicket[]).push(t);
        }
        byUser.forEach((tickets: PlanningTicket[], uid: number) => {
            const user = (this.users.find(u => u.id === uid) || ({ id: uid } as any)) as User;
            const sorted = [...tickets].sort((a, b) => {
                const ad = moment((a as any).scheduledStart).valueOf();
                const bd = moment((b as any).scheduledStart).valueOf();
                if (ad !== bd) return ad - bd;
                // [T54082] Same start day: the ticket that ends earliest
                // claims the day first. This keeps already-planned tickets
                // in place when a new ticket is dropped on the same day —
                // the newcomer (which always runs longer, per the end-date
                // calculation) fills the leftover hours, mirroring what
                // calculateTicketEndDate assumed when it was dropped.
                const ae = (a as any).scheduledEnd ? moment((a as any).scheduledEnd).valueOf() : Infinity;
                const be = (b as any).scheduledEnd ? moment((b as any).scheduledEnd).valueOf() : Infinity;
                if (ae !== be) return ae - be;
                const ao = Number((a as any).order || 0);
                const bo = Number((b as any).order || 0);
                if (ao !== bo) return ao - bo;
                return Number(a.id) - Number(b.id);
            });
            const dateMap = new Map<string, [number, number][]>();
            const ticketMap = new Map<number, { date: Date; hours: number }[]>();
            byDate.set(uid, dateMap);
            byTicket.set(uid, ticketMap);
            const usedOnDate = new Map<string, number>();
            for (const t of sorted) {
                const tAny = t as any;
                const total = Number(tAny.estimatedHours || 0) > 0 ? Number(tAny.estimatedHours) : 8;
                let remaining = total;
                const entries: { date: Date; hours: number }[] = [];
                let cursor = moment(tAny.scheduledStart).toDate();
                let iter = 0;
                let barrenStreak = 0;
                while (remaining > 0.01 && iter < 400) {
                    iter++;
                    const iso = moment(cursor).format('YYYY-MM-DD');
                    const free = this.dayCapacityHours(user, null, cursor) - (usedOnDate.get(iso) || 0);
                    if (free > 0.01) {
                        const take = Math.min(free, remaining);
                        usedOnDate.set(iso, (usedOnDate.get(iso) || 0) + take);
                        if (!dateMap.has(iso)) dateMap.set(iso, []);
                        (dateMap.get(iso) as [number, number][]).push([Number(t.id), take]);
                        entries.push({ date: new Date(cursor), hours: take });
                        remaining -= take;
                        barrenStreak = 0;
                    } else if (++barrenStreak > 60) {
                        // ~2 months without a single fillable day (dev lost
                        // their allocation): stop walking, the remainder is
                        // squeezed onto the last day at render time.
                        break;
                    }
                    cursor = addDays(cursor, 1);
                }
                ticketMap.set(Number(t.id), entries);
            }
        });
        return { byDate, byTicket };
    }

    // Get hours already scheduled for a dev on a specific date (excluding a ticket)
    // [T54082] Derived from the fill-the-day distribution instead of assuming
    // every ticket spreads its hours evenly across its own scheduled span.
    getScheduledHoursForDate(userId: number, date: Date, excludeTicketId?: number): number {
        const day = this.userDayHours.byDate.get(Number(userId))?.get(moment(date).format('YYYY-MM-DD'));
        if (!day) return 0;
        let total = 0;
        for (const [tid, hours] of day) {
            if (excludeTicketId && tid === excludeTicketId) continue;
            total += hours;
        }
        return total;
    }

    // Get remaining capacity (hours) for a dev on a date
    // [T54082] Day capacity via dayCapacityHours, planned hours via the
    // fill-the-day distribution — so a 4h ticket on an 8h day leaves 4h.
    getRemainingCapacity(user: User, allocation: DeveloperAllocation | null, date: Date, excludeTicketId?: number): number {
        const hpd = this.dayCapacityHours(user, allocation, date);
        if (hpd <= 0) return 0;
        const scheduled = this.getScheduledHoursForDate(user.id as number, date, excludeTicketId);
        return Math.max(hpd - scheduled, 0);
    }

    // Calculate end date accounting for existing scheduled work
    calculateTicketEndDate(user: User, allocation: DeveloperAllocation | null, startDate: Date, estimatedHours: number, excludeTicketId?: number): Date {
        const startAlloc = this.allocationOn(user, startDate) || allocation;
        const dayOfWeek = startDate.getDay() === 0 ? 6 : startDate.getDay() - 1;
        const entries = startAlloc ? ((startAlloc as any).entries?.models || (startAlloc as any).entries || []) : [];
        const dayEntries = entries.filter((e: any) => e.dayOfWeek === dayOfWeek);
        const hpd = dayEntries.reduce((s: number, e: any) => s + (e.hours || 0), 0) || 8;
        let remaining = estimatedHours > 0 ? estimatedHours : hpd;
        let current = new Date(startDate);
        let lastWorkDate = new Date(startDate);
        let iter = 0;
        while (remaining > 0 && iter < 365) {
            iter++;
            const avail = this.getRemainingCapacity(user, allocation, current, excludeTicketId);
            if (avail > 0) {
                const hoursThisDay = Math.min(remaining, avail);
                remaining -= hoursThisDay;
                lastWorkDate = new Date(current);
            }
            current = addDays(current, 1);
        }
        return lastWorkDate;
    }

    // Find the next date where the dev has remaining capacity
    findNextAvailableDate(user: User, allocation: DeveloperAllocation | null, fromDate: Date, excludeTicketId?: number): Date {
        let current = new Date(fromDate);
        let iter = 0;
        while (iter < 365) {
            const avail = this.getRemainingCapacity(user, allocation, current, excludeTicketId);
            if (avail > 0) return current;
            current = addDays(current, 1);
            iter++;
        }
        return fromDate;
    }

    // Schedule a ticket on a developer starting at a specific date (auto: respects capacity)
    @action scheduleTicket = async (ticketId: number, userId: number, startDate: Date) => {
        const ticket = (this.store.models as PlanningTicket[]).find(t => t.id === ticketId) as any;
        if (!ticket) return;
        const dev = this.users.find(u => u.id === userId);
        if (!dev) return;
        const alloc = this.allocationOn(userId, startDate);
        if (!alloc) {
            // [T54157] No allocation — cannot schedule; the dev must be
            // allocated to the project first (green days on the timeline).
            this.showDropRejection('This developer has no allocation — allocate them to the project first.');
            return;
        }
        // Snap to next available working day (exclude this ticket from capacity calc)
        const actualStart = this.findNextAvailableDate(dev, alloc, startDate, ticketId);
        const endDate = this.calculateTicketEndDate(dev, alloc, actualStart, ticket.estimatedHours || 8, ticketId);
        try {
            await api.patch('planning_ticket/' + ticketId + '/', {
                assignee: userId,
                scheduled_start: moment(actualStart).format('YYYY-MM-DD'),
                scheduled_end: moment(endDate).format('YYYY-MM-DD'),
                status: ticket.status === 'todo' ? 'in_progress' : ticket.status,
            });
            await this.store.fetch();
        } catch (e: any) {
            console.error('scheduleTicket error:', e);
        }
    };

    // Move an already-assigned ticket to a new start date (keeps same assignee)
    @action moveTicketDate = async (ticketId: number, startDate: Date) => {
        const ticket = (this.store.models as PlanningTicket[]).find(t => t.id === ticketId) as any;
        if (!ticket) return;
        // [T54157] Only allow dropping on days the dev is allocated to this
        // project; otherwise snap forward to the next such day.
        const userId = (ticket.assignee as any)?.id ?? ticket.assignee;
        const dev = this.users.find(u => u.id === userId);
        const alloc = this.allocationOn(userId, startDate);
        let actual = new Date(startDate);
        const startStatus = this.getDayStatus(actual, dev, alloc);
        if (startStatus !== 'available' && startStatus !== 'partial') {
            actual = this.findNextAvailableDate(dev, alloc, actual, ticketId);
        }
        const endDate = this.calculateTicketEndDate(dev, alloc, actual, ticket.estimatedHours || 8, ticketId);
        try {
            await api.patch('planning_ticket/' + ticketId + '/', {
                scheduled_start: moment(actual).format('YYYY-MM-DD'),
                scheduled_end: moment(endDate).format('YYYY-MM-DD'),
            });
            await this.store.fetch();
        } catch (e: any) {
            console.error('moveTicketDate error:', e);
        }
    };

    // Force-schedule: assigns to the exact date.
    // [T54157] Guard: only allowed on days the dev is allocated to this project.
    @action forceScheduleTicket = async (ticketId: number, userId: number, startDate: Date) => {
        const ticket = (this.store.models as PlanningTicket[]).find(t => t.id === ticketId) as any;
        if (!ticket) return;
        const dev = this.users.find(u => u.id === userId);
        if (!dev) return;
        const alloc = this.allocationOn(userId, startDate);
        const status = this.getDayStatus(startDate, dev, alloc);
        if (status !== 'available' && status !== 'partial') {
            this.showDropRejection('Cannot schedule on ' + moment(startDate).format('ddd D MMM') + ' — not an allocated day for this developer.');
            return;
        }
        const hours = ticket.estimatedHours || 8;
        // [T54082] Keep the forced start date, but compute the end date by
        // filling the dev's days (allocation minus already-planned work)
        // instead of assuming fixed 8h days.
        const endDate = this.calculateTicketEndDate(dev, alloc, startDate, hours, ticketId);
        try {
            await api.patch('planning_ticket/' + ticketId + '/', {
                assignee: userId,
                scheduled_start: moment(startDate).format('YYYY-MM-DD'),
                scheduled_end: moment(endDate).format('YYYY-MM-DD'),
                status: ticket.status === 'todo' ? 'in_progress' : ticket.status,
            });
            await this.store.fetch();
        } catch (e: any) {
            console.error('forceScheduleTicket error:', e);
        }
    };

    // Handle ticket drop on a dev timeline row
    @action onTicketDrop = async (e: React.DragEvent, userId: number) => {
        e.preventDefault();
        e.stopPropagation();
        const ticketId = Number((e as any).dataTransfer?.getData('text/plain') || (window as any).__cpDragTicketId);
        if (!ticketId) return;
        // Compute date from drop X position
        // [T54191] `currentTarget` (the grid row) scrolls along with the
        // timeline content, so `clientX - rect.left` is already the x in
        // content coordinates. The old code added scrollLeft on top of it,
        // double-counting the scroll offset and shifting drops by
        // floor(scrollLeft / dayWidth) days whenever the timeline was
        // scrolled horizontally.
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left - ROW_LABEL_W;
        const dayOffset = Math.floor(x / this.dayWidth);
        const dropDate = addDays(this.startDate, dayOffset);
        // If dragging from the timeline, just move the ticket date (same dev)
        const fromTimeline = (e as any).dataTransfer?.getData('application/x-drag-type') === 'timeline' || (window as any).__cpDragFromTimeline;
        if (fromTimeline) {
            await this.moveTicketDate(ticketId, dropDate);
            (window as any).__cpDragTicketId = null;
            (window as any).__cpDragFromTimeline = null;
            return;
        }
        // Sidebar drag: assign to dev + schedule
        // [T54157] Tickets can only be scheduled on days the developer is
        // allocated to THIS project (green/amber days). Force-booking
        // red/unavailable days is no longer allowed.
        const dev = this.users.find(u => u.id === userId);
        const alloc = this.allocationOn(userId, dropDate);
        const dropStatus = this.getDayStatus(dropDate, dev, alloc);
        if (dropStatus !== 'available' && dropStatus !== 'partial') {
            this.showDropRejection(
                'Cannot schedule on ' + moment(dropDate).format('ddd D MMM') +
                ' — this developer is not allocated to this project on that day (' + dropStatus.replace('-', ' ') + '). Drop on a green or amber day.'
            );
            (window as any).__cpDragTicketId = null;
            (window as any).__cpDragFromTimeline = null;
            return;
        }
        if (dev && alloc) {
            await this.scheduleTicket(ticketId, userId, dropDate);
        } else {
            this.showDropRejection('This developer has no allocation — allocate them to the project first (their days show red).');
        }
        // Clean up
        (window as any).__cpDragTicketId = null;
        (window as any).__cpDragFromTimeline = null;
    };

    @action showDropRejection = (message: string) => {
        this.dropRejection = { message, at: Date.now() };
        // auto-dismiss after 5s
        setTimeout(() => { if (this.dropRejection && Date.now() - this.dropRejection.at >= 4900) this.dropRejection = null; }, 5000);
    };

    @action onTicketDragStart = (e: React.DragEvent, ticketId: number, fromTimeline = false) => {
        (e as any).dataTransfer?.setData('text/plain', String(ticketId));
        (e as any).dataTransfer?.setData('application/x-drag-type', fromTimeline ? 'timeline' : 'sidebar');
        (window as any).__cpDragTicketId = ticketId;
        (window as any).__cpDragFromTimeline = fromTimeline;
    };

    @action onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };

    // Add ticket and auto-place on first available dev
    @action addTicket = async () => {
        if (!this.milestoneId) return;
        try {
            const res = await api.post('planning_ticket/', { title: 'New Ticket', milestone: this.milestoneId, status: 'todo', estimated_hours: 8 });
            await this.store.fetch();
            const newId = res.data?.id || res.id;
            if (newId && this.allocatedDevelopers.length > 0) {
                const { user, allocation } = this.allocatedDevelopers[0];
                const startDate = this.findNextAvailableDate(user, allocation, new Date(), newId);
                const endDate = this.calculateTicketEndDate(user, allocation, startDate, 8, newId);
                try {
                    await api.patch('planning_ticket/' + newId + '/', {
                        assignee: user.id,
                        scheduled_start: moment(startDate).format('YYYY-MM-DD'),
                        scheduled_end: moment(endDate).format('YYYY-MM-DD'),
                        status: 'in_progress',
                    });
                    await this.store.fetch();
                } catch (e: any) { console.error('auto-place error:', e); }
            }
        } catch (e: any) { console.error('addTicket error:', e); }
    };

    @action openEditTicketModal = (ticket: PlanningTicket) => { this.editingTicket = ticket; this.editModalOpen = true; };
    @action closeEditTicketModal = () => { this.editModalOpen = false; this.editingTicket = null; };
    @action afterTicketSave = async () => { this.closeEditTicketModal(); await this.store.fetch(); };
    @action selectTicket = (ticket: PlanningTicket) => { this.editingTicket = ticket; this.ticketPanelOpen = true; };
    @action closeTicketPanel = () => { this.ticketPanelOpen = false; this.editingTicket = null; };
    @action afterTicketPanelChange = async () => { await this.store.fetch(); };
    @action openAllocModal = (alloc: DeveloperAllocation) => { this.editingAllocation = alloc; this.allocModalOpen = true; };
    @action closeAllocModal = () => { this.allocModalOpen = false; this.editingAllocation = null; };
    @action afterAllocSave = async () => { this.closeAllocModal(); await this.allocationStore.fetch(); };
    @action openAddAllocModal = () => {
        // Create a new DeveloperAllocation model (not yet saved)
        const newAlloc = new DeveloperAllocation({});
        this.editingAllocation = newAlloc;
        this.allocModalOpen = true;
    };

    getDevAvailability(user: User, allocation: DeveloperAllocation | null): string {
        if (!allocation) return 'Team member (no allocation)';
        const pid = this.milestone?.project?.id;
        const entries = (allocation as any).entries?.models || (allocation as any).entries || [];
        const projectEntries = entries.filter((e: any) => Number(e.project?.id ?? e.project) === Number(pid));
        if (projectEntries.length === 0) return 'Allocated to other project';
        const totalHours = projectEntries.reduce((s: number, e: any) => s + (e.hours || 0), 0);
        const daySet = new Set(projectEntries.map((e: any) => e.dayOfWeek));
        const dayLabels = Array.from(daySet).sort().map((d: number) => DAY_SHORT_MAP[d] || '?').join(', ');
        return totalHours + 'h/week \u00B7 ' + (dayLabels || 'Not allocated');
    }

    getAllocBarPos(alloc: DeveloperAllocation | null): { left: number; width: number } | null {
        if (!alloc) return null;
        const start = (alloc as any).startDate ? moment((alloc as any).startDate).toDate() : null;
        const end = (alloc as any).endDate ? moment((alloc as any).endDate).toDate() : null;
        if (!start) return null;
        const left = daysBetween(this.startDate, start) * this.dayWidth;
        const width = end ? Math.max((daysBetween(start, end) + 1) * this.dayWidth, 60) : 5 * this.dayWidth;
        return { left, width };
    }
    // Returns per-day blocks for a ticket — one block per day the ticket
    // fills, with the day's hours shown in the block badge.
    // [T54082] Hours come from the fill-the-day distribution: days are
    // filled to (capacity − already-planned work) before hours spill into
    // the next day, so a 10h ticket on an 8h/day dev shows 8h + 2h (was:
    // an even 5h + 5h spread), and with 4h already planned on day one the
    // next ticket shows 4h there and 6h the day after. Hours that no
    // longer fit inside the ticket's scheduled span (overbooked dev /
    // shrunk allocation) are squeezed onto the last day so the badge
    // total stays honest.
    getTicketDayBlocks(ticket: PlanningTicket, user: User, allocation: DeveloperAllocation | null): { left: number; width: number; date: Date; hours: number; heightFraction: number; isLast: boolean }[] {
        const tAny = ticket as any;
        const start = tAny.scheduledStart ? moment(tAny.scheduledStart).toDate() : null;
        if (!start) return [];
        const end = tAny.scheduledEnd ? moment(tAny.scheduledEnd).toDate() : null;
        const fill = this.userDayHours.byTicket.get(Number(user.id))?.get(Number(ticket.id)) || [];
        const placed: { date: Date; hours: number }[] = [];
        let squeezed = 0;
        for (const e of fill) {
            if (end && e.date > end) { squeezed += e.hours; continue; }
            placed.push({ date: e.date, hours: e.hours });
        }
        if (squeezed > 0) {
            if (placed.length > 0) placed[placed.length - 1].hours += squeezed;
            // Entire fill landed beyond the scheduled span — show the
            // overbooking on the ticket's scheduled start date.
            else placed.push({ date: new Date(start), hours: squeezed });
        }
        if (placed.length === 0) {
            // No fillable capacity found at all (dev without allocation, or
            // nothing free within the horizon): fall back to an even spread
            // across the scheduled span so the ticket stays visible.
            return this.getEvenDayBlocksFallback(ticket, user, allocation, start, end);
        }
        return placed.map((b, i) => ({
            left: daysBetween(this.startDate, b.date) * this.dayWidth,
            width: this.dayWidth,
            date: b.date,
            hours: b.hours,
            heightFraction: Math.min(b.hours / (this.dayCapacityHours(user, allocation, b.date) || 8), 1),
            isLast: i === placed.length - 1,
        }));
    }

    // [T54082] Even-spread fallback for tickets that found no fillable
    // capacity (unallocated dev) — keeps the old rendering so legacy
    // tickets remain visible on the timeline.
    getEvenDayBlocksFallback(ticket: PlanningTicket, user: User, allocation: DeveloperAllocation | null, start: Date, end: Date | null): { left: number; width: number; date: Date; hours: number; heightFraction: number; isLast: boolean }[] {
        const endDate = end || addDays(start, Math.max(Math.ceil(((ticket as any).estimatedHours || 8) / 8), 1) - 1);
        const totalHours = (ticket as any).estimatedHours || 8;
        const blocks: { left: number; width: number; date: Date; hours: number; heightFraction: number; isLast: boolean }[] = [];
        let remainingHours = totalHours;
        let currentDate = new Date(start);
        let workingDayCount = 0;
        // First pass: count working days to distribute hours
        let countingDate = new Date(start);
        while (countingDate <= endDate) {
            const status = this.getDayStatus(countingDate, user, allocation);
            if (status !== 'weekend' && status !== 'holiday') workingDayCount++;
            countingDate = addDays(countingDate, 1);
        }
        if (workingDayCount === 0) workingDayCount = 1;
        const hoursPerDay = Math.max(totalHours / workingDayCount, 1);
        while (currentDate <= endDate) {
            const status = this.getDayStatus(currentDate, user, allocation);
            // Skip holidays and weekends (unless allocated on that weekend day)
            if (status === 'holiday') { currentDate = addDays(currentDate, 1); continue; }
            // Render the ticket block on any day the dev is scheduled,
            // even if not allocated to this project (not-allocated, other-project, unavailable)
            const isWorkingDay = status === 'available' || status === 'partial'
                || status === 'not-allocated' || status === 'other-project' || status === 'unavailable';
            if (isWorkingDay && remainingHours > 0) {
                const hours = Math.min(hoursPerDay, remainingHours);
                remainingHours -= hours;
                blocks.push({
                    left: daysBetween(this.startDate, currentDate) * this.dayWidth,
                    width: this.dayWidth,
                    date: new Date(currentDate),
                    hours,
                    heightFraction: Math.min(hours / (this.dayCapacityHours(user, allocation, currentDate) || 8), 1),
                    isLast: remainingHours <= 0,
                });
            }
            currentDate = addDays(currentDate, 1);
        }
        return blocks;
    }

    // Assign lane indices to tickets so overlapping day-blocks stack vertically.
    // Returns a map of ticket.id → lane number (0-based).
    getTicketLanes(tickets: PlanningTicket[], user: User, allocation: DeveloperAllocation | null): Map<number, number> {
        const lanes: Map<number, number> = new Map();
        // Track which dates each lane is occupied
        const laneOccupied: Set<string>[] = [];
        for (const ticket of tickets) {
            const blocks = this.getTicketDayBlocks(ticket, user, allocation);
            if (blocks.length === 0) continue;
            const occupiedDates = new Set(blocks.map(b => b.date.toDateString()));
            // Find first free lane
            const laneOccupiedArr = laneOccupied;
            const isLaneBusy = (laneIdx: number) =>
                [...occupiedDates].some(d => laneOccupiedArr[laneIdx].has(d));
            let lane = 0;
            while (lane < laneOccupied.length && isLaneBusy(lane)) {
                lane++;
            }
            if (lane >= laneOccupied.length) laneOccupied.push(new Set());
            occupiedDates.forEach(d => laneOccupied[lane].add(d));
            lanes.set(ticket.id, lane);
        }
        return lanes;
    }

    // ── [T54157] Priority queues section (sidebar) ──
    renderPriorityQueues(): React.ReactNode {
        if (!this.milestone) return null;
        const unassigned = this.pqQueuedTicketsFor('unassigned');
        const devs = this.pqAllocatedDevs;
        return (
            <PqSection data-test-ms-pq-section>
                <PqHeader>
                    <span>Priority Queues</span>
                    <button
                        className="btn btn-primary btn-sm"
                        data-test-ms-pq-schedule
                        onClick={() => this.saveQueues()}
                        disabled={this.queuesSaving}
                    >
                        {this.queuesSaving ? 'Scheduling…' : 'Schedule queues'}
                    </button>
                </PqHeader>
                {this.queuesMessage && (
                    <div className={'pq-message ' + this.queuesMessage.kind} data-test-ms-pq-message>
                        {this.queuesMessage.text}
                    </div>
                )}
                <div
                    className={'pq-queue pq-unassigned' + (this.pqDragOverQueue === 'unassigned' ? ' drag-over' : '')}
                    data-test-ms-pq-unassigned
                    onDragOver={e => { e.preventDefault(); this.setPqDragOver('unassigned'); }}
                    onDragLeave={() => { this.setPqDragOver(null); this.setPqInsertHint(null); }}
                    onDrop={e => this.onPqDrop(e, 'unassigned')}
                >
                    <div className="pq-queue-title">Unassigned ({unassigned.length})</div>
                    {unassigned.map((tk, idx) => this.renderPqChip(tk, 'unassigned', idx))}
                    {unassigned.length === 0 && <div className="pq-empty">All tickets assigned</div>}
                </div>
                {devs.map(({ user }) => {
                    const devTickets = this.pqQueuedTicketsFor(user.id);
                    const finish = this.pqQueueFinish(user.id);
                    return (
                        <div
                            key={user.id}
                            className={'pq-queue' + (this.pqDragOverQueue === user.id ? ' drag-over' : '')}
                            data-test-ms-pq-queue={user.id}
                            onDragOver={e => { e.preventDefault(); this.setPqDragOver(user.id); }}
                            onDragLeave={() => { this.setPqDragOver(null); this.setPqInsertHint(null); }}
                            onDrop={e => this.onPqDrop(e, user.id)}
                        >
                            <div className="pq-queue-title">
                                <div className="dev-avatar" style={{ background: userColor(user) }}>
                                    {(user as any).fullName?.charAt(0) || '?'}
                                </div>
                                <span>{(user as any).fullName}</span>
                                <span className="pq-queue-meta">
                                    {devTickets.length} ticket{devTickets.length === 1 ? '' : 's'}
                                    {finish && ` · finishes ${moment(finish).format('D MMM')}`}
                                </span>
                            </div>
                            {devTickets.map((tk, idx) => this.renderPqChip(tk, user.id, idx))}
                            {devTickets.length === 0 && <div className="pq-empty">Drop tickets here</div>}
                        </div>
                    );
                })}
                {this.pqInactiveAssignedDevs.map((user: any) => {
                    const devTickets = this.pqQueuedTicketsFor(user.id);
                    return (
                        <div
                            key={user.id}
                            className="pq-queue pq-inactive"
                            data-test-ms-pq-queue={user.id}
                            data-test-ms-pq-inactive
                        >
                            <div className="pq-queue-title">
                                <div className="dev-avatar" style={{ background: userColor(user) }}>
                                    {user.fullName?.charAt(0) || '?'}
                                </div>
                                <span>{user.fullName}</span>
                                <span className="pq-queue-meta">
                                    {devTickets.length} ticket{devTickets.length === 1 ? '' : 's'} · allocation ended
                                </span>
                            </div>
                            {devTickets.map((tk, idx) => this.renderPqChip(tk, user.id, idx))}
                        </div>
                    );
                })}
            </PqSection>
        );
    }

    renderPqChip(tk: any, queue: number | 'unassigned', idx?: number): React.ReactNode {
        const label = tk.title || `T${tk.number || tk.id}`;
        const est = tk.estimatedHours ?? tk.estimate ?? 0;
        // [T54191] Insert indicator while hovering another chip's top
        // (insert above) or bottom half (insert below).
        let hintCls = '';
        if (this.pqInsertHint && this.pqInsertHint.ticketId === tk.id) {
            hintCls = this.pqInsertHint.before ? ' drop-before' : ' drop-after';
        }
        return (
            <div
                key={tk.id}
                className={'pq-chip' + (tk.status === 'done' ? ' done' : '') + (idx !== undefined ? ' ranked' : '') + hintCls}
                data-test-ms-pq-chip={tk.id}
                draggable
                onDragStart={e => this.onPqDragStart(e, tk.id)}
                onDragEnd={() => { this.setPqDragOver(null); this.setPqInsertHint(null); }}
                onDragOver={e => this.onPqChipDragOver(e, tk.id)}
                onDrop={e => this.onPqDropAt(e, queue, tk.id)}
            >
                {idx !== undefined && <span className="pq-rank">{idx + 1}</span>}
                <span className="pq-chip-name">{label}</span>
                <span className="pq-chip-dates">{tk.scheduledEnd && moment(tk.scheduledEnd).format('D MMM')}</span>
                <span className="chip-hours">{est}h</span>
            </div>
        );
    }

    renderSidebarTicket(ticket: PlanningTicket): React.ReactNode {
        const tAny = ticket as any;
        const assignee = tAny.assignee;
        const aname = assignee ? ((assignee as any).fullName || ((assignee as any).firstName || '') + ' ' + ((assignee as any).lastName || '').trim()) : '';
        const initials = assignee ? ((assignee as any).firstName || '?').charAt(0) + ((assignee as any).lastName || '').charAt(0) : '?';
        const acolor = assignee ? colorFor(aname) : '#9ca3af';
        const isUnscheduled = !tAny.scheduledStart;
        const spent = Math.max(0, Number(tAny.spentHours) || 0);
        const est = Math.max(0, Number(tAny.estimatedHours) || 0);
        const pct = est > 0 ? Math.min(100, Math.round((spent / est) * 100)) : 0;
        let fill = '#d1d5db';
        if (tAny.status === 'done') fill = C.statusDone;
        else if (est > 0 && spent > est) fill = C.statusBlocked;
        else if (spent > 0) fill = C.statusInProgress;
        return (
            <TicketRow key={ticket.id} data-test-sidebar-ticket={ticket.id}
                draggable
                onDragStart={(e) => this.onTicketDragStart(e, ticket.id as number)}
                onClick={() => this.selectTicket(ticket)}
                style={{ cursor: isUnscheduled ? 'grab' : 'pointer', opacity: isUnscheduled ? 1 : 0.85 }}>
                <TicketRowHeader>
                    <TicketDot style={{ background: STATUS_DOT[tAny.status] || C.statusTodo }}>
                        {STATUS_ICON[tAny.status] || '○'}
                    </TicketDot>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {tAny.phabricatorTaskNumber ? 'T' + tAny.phabricatorTaskNumber + ' ' : ''}{tAny.title || ('#' + ticket.id)}
                    </span>
                    {tAny.scheduledStart && <TicketScheduledPill title={'Scheduled: ' + moment(tAny.scheduledStart).format('MMM D')}>{'\u{1F4C5}'}</TicketScheduledPill>}
                    {assignee && <TicketAvatar style={{ background: acolor }}>{initials}</TicketAvatar>}
                    <TicketHours data-test-ticket-hours={ticket.id}>{spent}/{est}h</TicketHours>
                </TicketRowHeader>
                <TicketProgress data-test-ticket-progress={ticket.id}
                    title={spent + 'h spent of ' + est + 'h estimated (' + pct + '%)'}>
                    <TicketProgressFill style={{ width: pct + '%', background: fill }} data-test-ticket-progress-fill={ticket.id} />
                </TicketProgress>
            </TicketRow>
        );
    }

    renderContent() {
        const dates = this.getTimelineDates();
        const devAllocs = this.teamDevelopers;
        const milestones = this.projectMilestones;
        const projectColor = this.projectColor;

        return (
            <Wrapper>
                {this.dropRejection && (
                    <DropRejection data-test-drop-rejection>{'⚠ ' + this.dropRejection.message}</DropRejection>
                )}
                <Toolbar>
                    <ActionBtn data-test-back-to-project onClick={this.backToProject} aria-label="Back to project" accent={C.accent}>
                        <span>← Back to Project</span>
                    </ActionBtn>
                    <span style={{ width: 1, background: C.border, height: 24, margin: '0 4px' }} />
                    <TBtn data-test-timeline-prev-week onClick={this.prevWeek} aria-label="Previous week">‹</TBtn>
                    <TBtn data-test-timeline-today onClick={this.goToCurrentWeek} aria-label="Jump to current week">Today</TBtn>
                    <TBtn data-test-timeline-next-week onClick={this.nextWeek} aria-label="Next week">›</TBtn>
                    <WeekLabel>{this.currentWeekLabel}</WeekLabel>
                    {this.milestone?.deadline && (() => {
                        const ds = this.deadlineStatus;
                        const dl = moment(this.milestone.deadline).format('MMM D');
                        const proj = this.projectedCompletion ? moment(this.projectedCompletion).format('MMM D') : '?';
                        return <>
                            <span style={{ width: 1, background: C.border, height: 24, margin: '0 4px' }} />
                            <DeadlineBadge status={ds} data-test-deadline-badge>
                                <OutlineIcon name={ds === 'overdue' || ds === 'at-risk' ? 'warning' : 'circle-check'} size={13} />
                                {' '}Deadline {dl} — {deadlineLabel(ds)}{proj && ds !== 'no-deadline' ? ` (projected: ${proj})` : ''}
                            </DeadlineBadge>
                        </>;
                    })()}
                    <span style={{ width: 1, background: C.border, height: 24, margin: '0 4px' }} />
                    <TBtn data-test-zoom-out onClick={this.zoomOut} aria-label="Zoom out">−</TBtn>
                    <span style={{ fontSize: 12, color: C.textSecondary, minWidth: 60, textAlign: 'center' }}>{this.zoomLabel}</span>
                    <TBtn data-test-zoom-in onClick={this.zoomIn} aria-label="Zoom in">+</TBtn>
                    <span style={{ flex: 1 }} />
                    <ToolbarGroup>
                        <ActionBtn data-test-add-ticket-btn onClick={this.addTicket} aria-label="Add ticket" accent={C.accent}>
                            <OutlineIcon name="plus" color={C.accent} />
                            <span>Add Ticket</span>
                        </ActionBtn>
                        <ActionBtn data-test-add-developer-btn onClick={this.openAddAllocModal} aria-label="Add developer allocation" accent={C.success}>
                            <OutlineIcon name="user-plus" color={C.success} />
                            <span>Add Developer</span>
                        </ActionBtn>
                    </ToolbarGroup>
                    <ToolbarDivider />
                    <ToolbarGroup>
                        <ActionBtn data-test-auto-allocate-btn onClick={this.autoAllocate} aria-label="Auto-allocate all tickets" accent="#805ad5">
                            <OutlineIcon name="bolt" color="#805ad5" />
                            <span>Auto-Allocate</span>
                        </ActionBtn>
                        <ActionBtn data-test-sync-phabricator-btn onClick={this.syncPhabricator} aria-label="Sync with Phabricator" accent="#6b46c1">
                            <OutlineIcon name="sync" color="#6b46c1" />
                            <span>Sync</span>
                        </ActionBtn>
                        <Link to={`/planning/milestone/${this.milestoneId}/status`} style={{ textDecoration: 'none' }}>
                            <ActionBtn data-test-status-overview-btn aria-label="Milestone status overview" accent="#319795">
                                <OutlineIcon name="chart-bars" color="#319795" />
                                <span>Status</span>
                            </ActionBtn>
                        </Link>
                    </ToolbarGroup>
                </Toolbar>

                <LegendBar>
                    <LegendItem><LegendDot style={{ background: C.successLight, border: '1px solid ' + C.success }} /> Available</LegendItem>
                    <LegendItem><LegendDot style={{ background: C.warningLight, border: '1px solid ' + C.warning }} /> Partial</LegendItem>
                    <LegendItem><LegendDot style={{ background: C.otherProject, border: '1px solid ' + C.otherProjectBorder }} /> Other project</LegendItem>
                    <LegendItem><LegendDot style={{ background: C.notAllocated, border: '1px solid ' + C.notAllocatedBorder }} /> Not allocated</LegendItem>
                    <LegendItem><LegendDot style={{ background: C.weekend, border: '1px solid ' + C.border }} /> Weekend</LegendItem>
                    <LegendItem><LegendDot style={{ background: C.unavailable, border: '1px solid ' + C.unavailableBorder }} /> Unavailable</LegendItem>
                    <LegendItem><LegendDot style={{ background: C.holiday, border: '1px solid #e0b4b4' }} /> Holiday</LegendItem>
                </LegendBar>

                <Body>
                    <Sidebar data-test-milestone-sidebar>
                        <SidebarHeader>
                            <span>Milestones &amp; Tickets</span>
                            <StatusBadgeRow data-test-status-counts>
                                {(['done', 'in_progress', 'review', 'todo', 'blocked'] as const).map(st => {
                                    const count = this.tickets.filter((t: any) => t.status === st).length;
                                    if (count === 0) return null;
                                    return <StatusCount key={st} color={STATUS_DOT[st]} title={count + ' ' + STATUS_LABEL[st]}>
                                        {STATUS_ICON[st]} {count}
                                    </StatusCount>;
                                })}
                            </StatusBadgeRow>
                        </SidebarHeader>
                        {this.renderPriorityQueues()}
                        {milestones.length === 0 && this.milestone && (
                            <MsGroup key={this.milestone.id}>
                                <MsHeader onClick={() => this.toggleMilestone(this.milestone ? this.milestone.id : 0)}>
                                    {this.expandedMilestones.has(this.milestone.id) ? '\u25BC' : '\u25B6'}
                                    <span>{this.milestone.name}</span>
                                    <MsMeta>{this.tickets.length} tickets</MsMeta>
                                </MsHeader>
                                {this.expandedMilestones.has(this.milestone.id) && this.tickets.map(tk => this.renderSidebarTicket(tk))}
                            </MsGroup>
                        )}
                        {milestones.map(ms => {
                            const msTickets = this.getMilestoneTickets(ms.id);
                            const done = msTickets.filter((t: any) => t.status === 'done').length;
                            const totalH = msTickets.reduce((s: number, t: any) => s + (t.estimatedHours || 0), 0);
                            const spentH = msTickets.reduce((s: number, t: any) => s + (Math.max(0, Number(t.spentHours) || 0)), 0);
                            // [T54191] Sub project / budget side: actually
                            // registered hours vs the sub project's own hour
                            // cap, plus the linked budget (spent_hours
                            // annotation on the sub project).
                            const msCap = (ms as any).budgetHours ?? null;
                            const msRegistered = Math.round((Number((ms as any).spentHours) || 0) * 10) / 10;
                            const msBudget = (ms as any).budget;
                            return (
                                <MsGroup key={ms.id}>
                                    <MsHeader onClick={() => this.toggleMilestone(ms.id)}>
                                        {this.expandedMilestones.has(ms.id) ? '\u25BC' : '\u25B6'}
                                        <span>{(ms as any).name}</span>
                                        <MsMeta data-test-ms-hours-progress={ms.id}>
                                            {done}/{msTickets.length} {'\u00B7'} {Math.round(spentH * 10) / 10}/{Math.round(totalH * 10) / 10}h
                                            {msCap !== null && (
                                                <span data-test-ms-cap={ms.id}> {'\u00B7'} {msRegistered}/{msCap}h</span>
                                            )}
                                            {msBudget && msBudget.id && (
                                                <span data-test-ms-budget={ms.id} title={msBudget.name} style={{ color: C.accent }}> {'\u00B7'} {'\u20AC'}</span>
                                            )}
                                        </MsMeta>
                                    </MsHeader>
                                    {this.expandedMilestones.has(ms.id) && msTickets.map(tk => this.renderSidebarTicket(tk))}
                                </MsGroup>
                            );
                        })}
                    </Sidebar>

                    <TimelineArea>
                        <TimelineScroll id="timeline-scroll-area">
                            <DateHeader>
                                <DateHeaderLabel>Developer</DateHeaderLabel>
                                {dates.map((date, i) => (
                                    <DateCell key={i} weekend={isWeekend(date)} today={isToday(date)} style={{ width: this.dayWidth }}>
                                        <span>{date.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}</span>
                                        <DateNum today={isToday(date)}>{date.getDate()}</DateNum>
                                    </DateCell>
                                ))}
                            </DateHeader>

                            {devAllocs.map(({ user, allocation }, idx: number) => {
                                const uAny = user as any;
                                const uname = uAny.fullName || (uAny.firstName || '') + ' ' + (uAny.lastName || '').trim();
                                const initials = (uAny.firstName || '?').charAt(0) + (uAny.lastName || '').charAt(0);
                                const ucolor = colorFor(uname);
                                const devTickets = this.getDevTickets(user.id);
                                // [T54194] Other-project tickets for this dev — faint
                                // outlines that also take a lane, so they never overlap
                                // the dev's own ticket bars.
                                const ghostTickets = this.getDevOtherProjectTickets(user.id);
                                const laneTickets = [...devTickets, ...ghostTickets];
                                const avail = this.getDevAvailability(user, allocation);
                                return (
                                    <GridRow key={user.id} data-test-timeline-row={user.id} stripe={(idx % 2) === 1}
                                        onDragOver={this.onDragOver}
                                        onDrop={(e) => this.onTicketDrop(e, user.id)}
                                        style={{ height: (() => {
                                            const lanes = this.getTicketLanes(laneTickets, user, allocation);
                                            const maxLane = Math.max(0, ...laneTickets.map((t: any) => lanes.get(t.id) ?? 0));
                                            return Math.max(80, (maxLane + 1) * 38 + 12);
                                        })() }}>
                                        <RowLabel onClick={() => { if (allocation) this.openAllocModal(allocation); }} data-test-dev-row={user.id}>
                                            <Avatar style={{ background: ucolor, opacity: allocation ? 1 : 0.5 }}>{initials}</Avatar>
                                            <DevInfo>
                                                <DevName>{uname}</DevName>
                                                <DevAllocInfo>{avail}</DevAllocInfo>
                                            </DevInfo>
                                        </RowLabel>
                                        {dates.map((date, i) => {
                                            const status = this.getDayStatus(date, user, allocation);
                                            // [T54194] Other-project info for this dev on this day
                                            const otherPrj = this.otherProjectInfo(user, date);
                                            const otherTix = this.otherProjectTickets(user, date);
                                            const hasOther = otherPrj.length > 0 || otherTix.length > 0;
                                            // Faint tint of the dominant other project's color
                                            let otherTint = status === 'other-project' ? '#e8edf5' : null;
                                            if (hasOther && status !== 'weekend' && status !== 'holiday') {
                                                const dom = [...otherPrj].sort((a, b) => b.hours - a.hours)[0]
                                                    || { name: (otherTix[0] || {}).projectName || '' };
                                                const col = colorFor(dom.name);
                                                otherTint = hexTint(col, 0.16);
                                            }
                                            let bg: string;
                                            if (status === 'weekend') bg = C.weekend;
                                            else if (status === 'holiday') bg = C.holiday;
                                            else if (status === 'unavailable') bg = '#fde8e8';
                                            else if (status === 'not-allocated') bg = 'repeating-linear-gradient(-45deg, rgba(100,100,100,0.08), rgba(100,100,100,0.08) 4px, transparent 4px, transparent 8px)';
                                            else if (otherTint !== null) bg = otherTint;
                                            else if (status === 'partial') bg = C.warningLight;
                                            else if (status === 'available') bg = C.successLight;
                                            else bg = 'transparent';
                                            const tipParts: string[] = [];
                                            for (const p of otherPrj) tipParts.push(p.name + ' — ' + p.hours + 'h allocated');
                                            for (const t of otherTix) tipParts.push('Ticket: ' + t.title + ' (' + t.projectName + ', ~' + t.hoursLeft + 'h/day)');
                                            return (
                                                <GridCell key={i} status={status} style={{ width: this.dayWidth, background: bg, position: 'relative' }}
                                                    data-test-grid-cell={`${user.id}-${i}`}>
                                                    {hasOther && (
                                                        <span
                                                            data-test-other-project-icon={`${user.id}-${i}`}
                                                            onMouseEnter={e => this.showHoverTip(e, tipParts)}
                                                            onMouseMove={e => this.showHoverTip(e, tipParts)}
                                                            onMouseLeave={this.hideHoverTip}
                                                            style={{
                                                                position: 'absolute', right: 2, bottom: 2,
                                                                width: 8, height: 8, borderRadius: '50%',
                                                                background: colorFor((otherPrj[0] || otherTix[0] || { projectName: '?' } as any).name || (otherTix[0] || {} as any).projectName || C.otherProjectBorder),
                                                                border: '1px solid rgba(0,0,0,0.25)',
                                                                opacity: 0.8,
                                                                cursor: 'help',
                                                            }} />
                                                    )}
                                                </GridCell>
                                            );
                                        })}
                                        {/* Float-style task bars — one per day, full lane height, stacked */}
                                        {(() => {
                                            const lanes = this.getTicketLanes(laneTickets, user, allocation);
                                            const maxLane = Math.max(0, ...laneTickets.map((t: any) => lanes.get(t.id) ?? 0));
                                            const laneGap = 4;
                                            const topPadding = 6;
                                            const availStripH = 6;
                                            const availableH = (rowHeight: number) => rowHeight - topPadding - availStripH;
                                            const rowH = Math.max(80, (maxLane + 1) * 38 + topPadding + availStripH);
                                            const laneH = (availableH(rowH) - laneGap * maxLane) / (maxLane + 1);
                                            return <>
                                                {devTickets.map((ticket: any) => {
                                                const blocks = this.getTicketDayBlocks(ticket, user, allocation);
                                                if (blocks.length === 0) return null;
                                                const lane = lanes.get(ticket.id) ?? 0;
                                                const isSelected = this.editingTicket?.id === ticket.id;
                                                const top = topPadding + lane * (laneH + laneGap);
                                                return blocks.map((block, blockIdx) => {
                                                    const opacity = ticket.status === 'done' ? 0.6 : 1;
                                                    return (
                                                        <div
                                                            key={ticket.id + '-' + blockIdx}
                                                            data-test-ticket-bar={ticket.id}
                                                            draggable
                                                            onDragStart={(e: any) => { e.stopPropagation(); this.onTicketDragStart(e, ticket.id, true); }}
                                                            onDragEnd={() => { (window as any).__cpDragFromTimeline = null; }}
                                                            style={{
                                                                left: block.left + ROW_LABEL_W + 2,
                                                                width: block.width - 4,
                                                                height: laneH,
                                                                top,
                                                                background: projectColor,
                                                                opacity,
                                                                borderRadius: 5,
                                                                position: 'absolute',
                                                                cursor: 'grab',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                padding: '0 8px',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 500,
                                                                color: '#fff',
                                                                overflow: 'hidden',
                                                                whiteSpace: 'nowrap',
                                                                textOverflow: 'ellipsis',
                                                                boxShadow: isSelected ? '0 0 0 2px #fff, 0 0 0 4px ' + projectColor : '0 1px 3px rgba(0,0,0,0.15)',
                                                                transition: 'box-shadow 0.15s, transform 0.15s',
                                                                zIndex: isSelected ? 6 : 3,
                                                            }}
                                                            onClick={(e: any) => { e.stopPropagation(); this.selectTicket(ticket); }}
                                                            onMouseEnter={(e: any) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.25)'; }}
                                                            onMouseLeave={(e: any) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = isSelected ? '0 0 0 2px #fff, 0 0 0 4px ' + projectColor : '0 1px 3px rgba(0,0,0,0.15)'; }}
                                                            title={[ticket.title, (ticket.estimatedHours || 0) + 'h', STATUS_LABEL[ticket.status] || ticket.status, moment(block.date).format('ddd MMM D'), ticket.scheduledStart ? 'Sched: ' + moment(ticket.scheduledStart).format('MMM D') + ' \u2013 ' + moment(ticket.scheduledEnd).format('MMM D') : null].filter(Boolean).join(' \u00B7 ')}
                                                        >
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{ticket.phabricatorTaskNumber ? 'T' + ticket.phabricatorTaskNumber + ' ' : ''}{ticket.title || ('#' + ticket.id)}</span>
                                                            <span style={{ background: 'rgba(0,0,0,0.25)', padding: '1px 6px', borderRadius: 3, fontSize: '0.65rem', flexShrink: 0, marginLeft: '4px' }}>{block.hours.toFixed(0)}h</span>
                                                        </div>
                                                    );
                                                });
                                                })}
                                                {/* [T54194] Faint see-through outlines for the dev's
                                                    OTHER-project tickets — same lane geometry as the
                                                    real bars, but not interactive. */}
                                                {ghostTickets.map((ticket: any) => {
                                                    const blocks = this.getTicketDayBlocks(ticket, user, allocation);
                                                    if (blocks.length === 0) return null;
                                                    const lane = lanes.get(ticket.id) ?? 0;
                                                    const top = topPadding + lane * (laneH + laneGap);
                                                    const pName = (ticket.milestone as any)?.project?.name || 'Other project';
                                                    const col = colorFor(pName);
                                                    const s = ticket.scheduledStart ? moment(ticket.scheduledStart).format('D MMM') : '';
                                                    const e = ticket.scheduledEnd ? moment(ticket.scheduledEnd).format('D MMM') : '';
                                                    const tipLines = [
                                                        (ticket.phabricatorTaskNumber ? 'T' + ticket.phabricatorTaskNumber + ' ' : '') + (ticket.title || ('#' + ticket.id)),
                                                        'Other project: ' + pName,
                                                        (s + (e && e !== s ? ' – ' + e : '')) + (ticket.estimatedHours ? ' · ' + ticket.estimatedHours + 'h' : ''),
                                                    ];
                                                    return blocks.map((block: any, blockIdx: number) => (
                                                        <div
                                                            key={'ghost-' + ticket.id + '-' + blockIdx}
                                                            data-test-ghost-ticket={ticket.id}
                                                            onMouseEnter={ev => this.showHoverTip(ev, tipLines)}
                                                            onMouseMove={ev => this.showHoverTip(ev, tipLines)}
                                                            onMouseLeave={this.hideHoverTip}
                                                            style={{
                                                                left: block.left + ROW_LABEL_W + 2,
                                                                width: block.width - 4,
                                                                height: laneH,
                                                                top,
                                                                border: '1.5px dashed ' + hexTint(col, 0.75),
                                                                background: hexTint(col, 0.06),
                                                                borderRadius: 5,
                                                                position: 'absolute',
                                                                boxSizing: 'border-box',
                                                                cursor: 'help',
                                                                zIndex: 2,
                                                            }}
                                                        />
                                                    ));
                                                })}
                                            </>
                                        })()}
                                        {dates.some(isToday) && (
                                            <TodayLine style={{ left: daysBetween(this.startDate, new Date()) * this.dayWidth + ROW_LABEL_W }} />
                                        )}
                                        {this.milestone?.deadline && (() => {
                                            // [T54191] deadline is a Moment (Casts.date).
                                            const dl = ((this.milestone as any).deadline.toDate ? (this.milestone as any).deadline.toDate() : new Date((this.milestone as any).deadline));
                                            const offset = daysBetween(this.startDate, dl) * this.dayWidth + ROW_LABEL_W;
                                            const ds = this.deadlineStatus;
                                            const color = deadlineColor(ds);
                                            const dlLabel = moment(dl).format('MMM D');
                                            return <>
                                                <DeadlineLine style={{ left: offset, background: color }} />
                                                <DeadlineLabel style={{ left: offset + 6, background: color }}>{'⚠'} {dlLabel}</DeadlineLabel>
                                            </>;
                                        })()}
                                    </GridRow>
                                );
                            })}

                            {devAllocs.length === 0 && (
                                <NoDataMsg>
                                    No developers found. Assign a team to the project, allocate developers, or assign tickets to see them here.
                                    <br /><br />
                                    <button
                                        data-test-add-developer-empty-btn
                                        onClick={this.openAddAllocModal}
                                        style={{
                                            padding: '6px 16px', border: '1px solid ' + C.success, borderRadius: 4,
                                            background: C.success, color: '#fff', cursor: 'pointer', fontSize: 13,
                                        }}
                                    >
                                        + Add Developer Allocation
                                    </button>
                                </NoDataMsg>
                            )}
                        </TimelineScroll>
                        {/* [T54191] Drag handle: resize the burndown panel by
                            dragging, collapse/expand it via the chevron. */}
                        <BurndownHandle
                            data-test-burndown-handle
                            data-collapsed={this.burndownCollapsed ? 'true' : undefined}
                            title={this.burndownCollapsed ? 'Show the burndown chart' : 'Drag to resize the burndown chart'}
                            onMouseDown={this.onBurndownHandleMouseDown}
                            onClick={this.burndownCollapsed ? this.toggleBurndown : undefined}
                        >
                            {this.burndownCollapsed ? (
                                <BurndownCollapsedLabel>▲ Show burndown</BurndownCollapsedLabel>
                            ) : (
                                <>
                                    <BurndownGrip />
                                    <BurndownToggle
                                        data-test-burndown-toggle
                                        title="Hide the burndown chart"
                                        onMouseDown={e => e.stopPropagation()}
                                        onClick={this.toggleBurndown}
                                    >
                                        ▼
                                    </BurndownToggle>
                                </>
                            )}
                        </BurndownHandle>
                        {!this.burndownCollapsed && (
                            <BurndownBox data-test-burndown-box style={{ height: this.burndownHeight }}>
                                {/* ~50px is the chart's own header/padding */}
                                <BurndownChart milestoneId={this.milestoneId} chartHeight={Math.max(90, this.burndownHeight - 50)} />
                            </BurndownBox>
                        )}
                    </TimelineArea>
                </Body>

                {this.renderSidebars()}

                {this.editModalOpen && this.editingTicket ? (
                    <TicketEditModal model={this.editingTicket} open={this.editModalOpen}
                        onClose={this.closeEditTicketModal} afterSave={this.afterTicketSave}
                        trigger={<span style={{ display: 'none' }} />} />
                ) : null}
                {this.allocModalOpen && this.editingAllocation ? (
                    <AllocationEditModal model={this.editingAllocation} open={this.allocModalOpen}
                        onClose={this.closeAllocModal} afterSave={this.afterAllocSave}
                        allocations={this.allocationStore.models}
                        projectId={this.milestone?.project?.id} />
                ) : null}
                <TicketEditPanel
                    ticket={this.editingTicket}
                    visible={this.ticketPanelOpen}
                    users={this.users}
                    onClose={this.closeTicketPanel}
                    onChanged={this.afterTicketPanelChange}
                />
                {/* [T54194] Custom hover popup (other-project indicators) */}
                {this.hoverTip && (
                    <HoverTip data-test-hover-tip style={{ left: this.hoverTip.x, top: this.hoverTip.y }}>
                        {this.hoverTip.lines.map((l, i) => (
                            <div key={i} style={i === 0 ? { fontWeight: 600 } : undefined}>{l}</div>
                        ))}
                    </HoverTip>
                )}
            </Wrapper>
        );
    }
}
